import { UpdateJobPermissions } from '../UpdateJobPermissions.js';
import { serverTimestamp } from 'firebase/database';
import {
  getFirebaseApp,
  getFirebaseFolderPath,
  createReplacingItemAttributesMutation,
  validateLocationInput,
  validateAndSnapLocationInput,
  getPhotoAssociatedItemRecord,
  createReplacingObjectMutation,
  getModificationTimestamp,
  getAuthData,
  getPhotoFileName,
  buildJobStyles,
  getDefaultSavedViews
} from './KatapultJobUtilities.js';
import { KatapultModel } from '../katapult-model/KatapultModel.js';
import {
  composeJobPropertyUpdate,
  composeCompanyPermissionUpdate,
  composeJobNameUpdate,
  composeMetadataAttributeUpdate,
  composeNodeDeleteUpdate,
  composeNodeLocationUpdate,
  composeConnectionDeleteUpdate,
  composeItemAttributesUpdate,
  composeSectionDeleteUpdate,
  composeSectionLocationUpdate,
  composeSectionsSnappedToConnectionPathUpdate,
  composeGeohashLocationUpdate,
  composeItemGeoStyleUpdate,
  composeProjectFolderUpdate,
  composeJobDoneStateUpdate,
  composeJobStatusUpdate,
  composeNodeCreationUpdate,
  composeItemPhotoAssociationUpdate,
  composePhotoAssociationValueUpdate,
  composePhotoUnassociationUpdate
} from './KatapultJobUpdateComposers.js';
import {
  composeAnchorMarkerUpdate,
  composePhotoElementCreate,
  composePhotoElementUpdate,
  composeTraceDataUpdate,
  getFlatPhotoElementList
} from './update-composers/KatapultPhotoUpdateComposers.js';
import { calcPhotoClearances } from '../photo-data/MRClearanceCalculations.js';
import { CalcPhotoDistanceRatios } from '../ApplyEffectiveMoves.js';
import { GeofireTools } from '../GeofireTools.js';
import { GoogleGeometry } from '../GoogleGeometry.js';
import { Convert } from '../Convert.js';
import { Round } from '../Round.js';
import { GetExif } from '../../modules/GetExif.js';
import { GetFolderSharing } from '../GetFolderSharing.js';
import { PickAnAttribute } from '../PickAnAttribute.js';
import { getClientOrAdminStorage } from '../firebase-helpers/ClientOrAdminStorage.js';
import { getJobListEntriesByName } from '../../../features/job-permissions/job-permissions-service.js';
import { getClearanceNodeData } from '../../modules/photo-data/MRClearances.js';

/** @import { ClientOrAdminFirebase } from '../types/ClientOrAdminFirebase.js' */

/** @import { CreateNodeData, CreateConnectionData, CreateSectionData, PhotoElementCreateParams, PhotoElementUpdateParams, AssociationValue } from './KatapultJobUtilities.js' */

/**
 * @import {
 *   JobDetails,
 *   PhotoType,
 * } from './KatapultJobUtilities.js';
 */

/**
 * @import { JobTraces, OnSnapshotCallback, PixelSelection, TraceDefinition, TraceItemsList } from './KatapultJobTypes.d.ts';
 */

/**
 * @template T - The type of data to be returned from a snapshot.
 * @callback OnSnapshotCustomCallback<T> Function signature for a custom snapshot callback.
 * @param {{ val: () => T }} snapshot - The snapshot of the data.
 * @returns {void}
 */

/**
 * @typedef {Object} KatapultJobOptions Options for the job object.
 * @property {ClientOrAdminFirebase} [firebaseApp] - The Firebase app to use for backend operations.
 * @property {boolean} [autoCache] - Automatically cache data on "gets" (use in conjunction with {@link KatapultJob.destroy} to remove
 * listeners).
 * @property {(path: string, update: object) => Promise<void>} [onWrite] - Optional callback invoked after every Firebase update, receiving
 * the root path and the update object that was written.
 */

/** Valid permissions to grant for a job */
const JOB_SHARING_PERMISSIONS = ['read', 'write', 'applicant', null];

/**
 * Normalize a pixel selection value to the expected single item array containing the object of coordinate values.
 * @param {PixelSelection|null|undefined|{ percentX: number, percentY: number }} pixelSelection - Pixel selection value.
 * @returns {PixelSelection|null|undefined} Normalized pixel selection value.
 */
function normalizePixelSelection(pixelSelection) {
  const pixelSelectionExists = pixelSelection != null;
  const pixelSelectionIsObject = typeof pixelSelection === 'object';
  const pixelSelectionIsNotAnArray = !Array.isArray(pixelSelection);
  const pixelSelectionContainsCoordinates =
    pixelSelectionExists && pixelSelectionIsObject && 'percentX' in pixelSelection && 'percentY' in pixelSelection;
  const isSinglePixelSelectionPoint =
    pixelSelectionExists && pixelSelectionIsObject && pixelSelectionIsNotAnArray && pixelSelectionContainsCoordinates;

  return isSinglePixelSelectionPoint ? [pixelSelection] : pixelSelection;
}

export class KatapultJob {
  static JOB_DATA_PATHS = [
    'name',
    'job_creator',
    'job_owner',
    'project_folder',
    'project_id',
    'status',
    'done',
    'map_styles',
    'metadata',
    'sharing'
  ];
  static JOB_DATA_PATH_ALIASES = { model: 'job_creator' };
  static MAP_PRINT_ANNOTATION_BATCH_SIZE = 2000;

  /**
   * The id (key) of the job.
   * @type {string}
   */
  #id;

  /**
   * Auto-cache job data.
   * @type {boolean}
   */
  #autoCache = false;

  /**
   * Record of snapshot listener lists by path.
   * @type {Record<string, OnSnapshotCallback[]>}
   */
  #listenersByPath = {};

  /**
   * Record of caching listeners by path.
   * @type {Record<string, OnSnapshotCallback>}
   */
  #cachingListenersByPath = {};

  /**
   * Optional callback invoked after every Firebase write.
   * @type {((path: string, update: object) => Promise<void>)|null}
   */
  #onWrite = null;

  /**
   * A Katapult Pro job data object.
   * @param {string} jobId - The id (key) of the job.
   * @param {KatapultJobOptions} [options] - Options for the data object.
   */
  constructor(jobId, options = {}) {
    this.#id = jobId;
    this.firebase = options?.firebaseApp ?? getFirebaseApp();
    this.#autoCache = options?.autoCache ?? false;
    this.#onWrite = options?.onWrite ?? null;
    this.database = this.firebase.database();
    this.reference = this.database.ref(`photoheight/jobs/${jobId}`);
  }

  get #jobPath() {
    return `photoheight/jobs/${this.#id}`;
  }

  /**
   * Perform a Firebase update and call the onWrite callback if one is set.
   * @param {string} path - The root Firebase path being updated
   * @param {object} update - The update object being written.
   * @returns {Promise<void>}
   */
  async #performUpdate(path, update) {
    const result = await this.database.ref(path).update(update);
    await this.#onWrite?.(path, update);
    return result;
  }

  /**
   * The id (key) of the job.
   * @type {string}
   */
  get id() {
    return this.#id;
  }

  /**
   * Whether auto-caching listeners will be set up on data "gets".
   * @type {boolean}
   */
  get autoCache() {
    return this.#autoCache;
  }

  /**
   * Get a new Katapult Job object from a job id.
   * @param {string} jobId - The id (key) of the job.
   * @param {KatapultJobOptions} [options] - Options for the job object.
   * @returns {KatapultJob} The job object.
   */
  static fromId(jobId, options) {
    return new KatapultJob(jobId, options);
  }

  /**
   * Create a new job.
   * @param {string} name - The name for the job.
   * @param {string} jobModelId - The model for the job.
   * @param {string} actorUserGroup - The active company of the user creating the job.
   * @param {object} optionalData - Optional data to set on the job.
   * @param {string|null} [optionalData.projectFolder] - The project folder for the job.
   * @param {string|null} [optionalData.projectId] - The project id for the job.
   * @param {string|object|null} [optionalData.mapStyles] - The map styles for the job (can be either an ID or a map styles object).
   * @param {object|null} [optionalData.sharing] - The sharing object for the job.
   * @param {object|null} [optionalData.metadata] - The metadata for the job.
   * @param {object|null} [optionalData.photoFolders] - The photo folders for the job.
   * @param {object} [options] - Options for the job creation.
   * @param {ClientOrAdminFirebase} [options.firebaseApp] - The Firebase app to use for backend operations.
   * @returns {Promise<KatapultJob>} The created job.
   */
  static async create(name, jobModelId, actorUserGroup, optionalData = {}, options = {}) {
    // Check for required parameters.
    if (!name || typeof name !== 'string') throw Error('Missing required parameter: name');
    if (!jobModelId || typeof jobModelId !== 'string') throw Error('Missing required parameter: jobModelId');
    if (!actorUserGroup || typeof actorUserGroup !== 'string') throw Error('Missing required parameter: actorUserGroup');

    // Extract optional data.
    const {
      projectFolder: inputProjectFolder = null,
      projectId = null,
      mapStyles = null,
      sharing = {},
      metadata = {},
      photoFolders = null
    } = optionalData;

    // Get a firebase database reference.
    const firebaseApp = options?.firebaseApp ?? getFirebaseApp();
    const database = firebaseApp.database();

    // Get the owner of the job's model
    const jobModel = KatapultModel.fromId(jobModelId, { firebaseApp });
    const jobOwner = (await jobModel.getModelOwner()) || actorUserGroup;
    const jobOwnerModel = KatapultModel.fromId(jobOwner, { firebaseApp });

    // Remove any leading/trailing whitespace from job name.
    name = name.trim();
    if (name.length === 0) throw Error('Job name cannot be empty');

    // Make sure a job with this name doesn't already exist for the actor's user group.
    const nameQueryResult = await getJobListEntriesByName(actorUserGroup, name, { firebase: firebaseApp });
    if (nameQueryResult) throw { code: 'job-already-exists' };

    // Set the project folder depending on job ownership.
    const projectFolder = jobOwner === actorUserGroup ? (inputProjectFolder ?? jobOwner) : jobOwner;

    // Build the job styles.
    const jobStyles = await buildJobStyles(mapStyles, jobModel, jobOwnerModel);

    // Set up the sharing object.
    Object.assign(sharing, { [actorUserGroup]: 'write', [jobOwner]: 'write' });

    // Check for a default tracking model.
    metadata.tracking_model ||= await jobModel.getDefaultActionTrackingModel();

    // Get the job id.
    const jobId = String(database.ref().push().key);

    const { savedViews, layers } = await getDefaultSavedViews(jobModel, jobId);

    // Create an update object and begin creating the job.
    const update = {};
    update[`jobs/${jobId}`] = {
      name,
      date_created: serverTimestamp(),
      job_creator: jobModelId,
      job_owner: jobOwner,
      project_folder: projectFolder,
      project_id: projectId,
      sharing,
      map_styles: jobStyles,
      photo_folders: photoFolders,
      metadata,
      saved_views: savedViews,
      layers
    };
    update[`active_job_list/${jobId}/name`] = name;

    // Add the job to each company's job list.
    const companyProjectFolderPath = getFirebaseFolderPath(projectFolder);
    for (const [companyId, permission] of Object.entries(sharing)) {
      const jobUpdate = {
        date_shared: serverTimestamp(),
        name: name,
        job_creator: jobModelId,
        job_owner: jobOwner,
        permission,
        status: 'active',
        metadata
      };
      await UpdateJobPermissions(jobId, actorUserGroup, jobUpdate, {
        updatePath: 'photoheight',
        skipAddingMainCompanyToSharing: true,
        wipeExistingData: true,
        sharing: { [companyId]: true },
        firebase: firebaseApp,
        update
      });

      // Add the job to the company's project folder if the job has a folder.
      update[`company_space/${companyId}/project_folders/${companyProjectFolderPath}/jobs/${jobId}`] = true;
      // Share the job model space with the company.
      update[`company_space/${jobModelId}/companies_with_model_access/${companyId}`] = true;
    }

    // Lock the model for deletion.
    update[`company_space/${jobModelId}/options/models_locked_for_deletion`] = true;

    // Perform the update.
    await database.ref('photoheight').update(update); // Doesn't use the onWrite hook because create is a static method and doesn't have access to instance properties.
    return KatapultJob.fromId(jobId, { firebaseApp });
  }

  /**
   * Destroy the job object, removing all listeners.
   * @returns {void}
   */
  destroy() {
    // Remove all listeners for each path.
    const listenersByPathEntries = Object.entries(this.#listenersByPath);
    for (const [path, listeners] of listenersByPathEntries) {
      for (const listener of listeners) {
        this.reference.child(path).off('value', listener);
      }
    }

    // Remove all caching listeners.
    const cachingListenersByPathEntries = Object.entries(this.#cachingListenersByPath);
    for (const [path, listener] of cachingListenersByPathEntries) {
      this.reference.child(path).off('value', listener);
    }

    // Clear the listener paths.
    this.#listenersByPath = {};
    this.#cachingListenersByPath = {};
  }

  /**
   * Get the job's name.
   * @returns {Promise<string>} The job's name.
   */
  async getName() {
    return await this.#readDataOnce('name');
  }

  /**
   * Listen for changes to the job's name.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's name changes.
   * @returns {() => void} A function to remove the listener.
   */
  onNameSnapshot(callback) {
    return this.#setUpListener('name', callback);
  }

  /**
   * Set the job's name.
   * @param {string} name - The name to set for the job.
   * @returns {Promise<void>} A promise that resolves when the name has been set.
   */
  async setName(name) {
    // Make sure the name is a string
    if (typeof name !== 'string') throw new Error(`Invalid name: ${name}`);

    // Check to see if the user can rename the job
    const jobOwner = await this.getOwner();

    const { root_company: userRootCompany, user_group: userActiveCompany } = (await getAuthData(this.firebase)) ?? {};
    if (userRootCompany != jobOwner && userActiveCompany != jobOwner) throw new Error('Only the Job Owner may rename this job');

    const snapshotParentJobId = await this.getSnapshotParentJobId();

    if (!snapshotParentJobId) {
      const alreadyExists = await getJobListEntriesByName(jobOwner, name, { firebase: this.firebase });
      if (alreadyExists) throw new Error('A job with this name already exists');
    }

    const jobSharing = await this.getSharing();

    // Create an update object and make the name update
    const photoheightUpdate = composeJobNameUpdate(name, {
      jobId: this.id,
      jobSharing,
      snapshotParentJobId
    });

    // Perform the update
    return await this.#performUpdate('photoheight', photoheightUpdate);
  }

  /**
   * Get the job's model.
   * @returns {Promise<any>} The job's model.
   */
  async getModel() {
    return await this.#readDataOnce('job_creator');
  }

  /**
   * Listen for changes to the job's model.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's model changes.
   */
  onModelSnapshot(callback) {
    return this.#setUpListener('job_creator', callback);
  }

  /**
   * Set the job's model.
   * @param {string} model - The model to set for the job.
   * @returns {Promise<void>} A promise that resolves when the model has been set.
   */
  async setModel(model) {
    // Make sure the model is a string
    if (typeof model !== 'string') throw new Error(`Invalid model: ${model}`);

    // Check to see if the user can change the model
    const jobOwner = await this.getOwner();
    const { root_company: userRootCompany, user_group: userActiveCompany } = (await getAuthData(this.firebase)) ?? {};
    if (userRootCompany != jobOwner && userActiveCompany != jobOwner)
      throw new Error('Only the Job Owner may change the model of this job');

    // Create an update object and make the model update
    const photoheightUpdate = composeJobPropertyUpdate('job_creator', model, { jobId: this.id, jobSharing: await this.getSharing() });

    // Perform the update
    return await this.#performUpdate('photoheight', photoheightUpdate);
  }

  /**
   * Get the attribute definitions ("other attributes") from the job's model, keyed by attribute name.
   * Each definition carries its `gui_element`, picklists, etc. This is the same map the legacy maps
   * expose as `otherAttributes`.
   * @returns {Promise<Record<string, any>>} The model's attribute definitions, or an empty object if the job has no model.
   */
  async getModelAttributes() {
    const jobModelId = await this.getModel();
    if (jobModelId == null) return {};
    const jobModel = KatapultModel.fromId(jobModelId, { firebaseApp: this.firebase });
    return (await jobModel.getAttributes()) ?? {};
  }

  /**
   * Set's the job's map styles.
   * @param {string|object|null} mapStyles - The map styles for the job (can be either an ID or a map styles object).
   */
  async setMapStyles(mapStyles) {
    const jobModelId = await this.getModel();
    const jobModel = KatapultModel.fromId(jobModelId, { firebaseApp: this.firebase });
    const jobOwner = await this.getOwner();
    const ownerModel = KatapultModel.fromId(jobOwner, { firebaseApp: this.firebase });
    const jobStyles = await buildJobStyles(mapStyles, jobModel, ownerModel);

    return await this.#performUpdate(this.#jobPath, { map_styles: jobStyles });
  }

  /**
   * Get the job's owner.
   * @returns {Promise<string>} The job's owner.
   */
  async getOwner() {
    return await this.#readDataOnce('job_owner');
  }

  /**
   * Listen for changes to the job's owner.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's owner changes.
   * @returns {() => void} A function to remove the listener.
   */
  onOwnerSnapshot(callback) {
    return this.#setUpListener('job_owner', callback);
  }

  /**
   * Set the job's owner.
   * @param {string} company - The owner to set for the job.
   * @returns {Promise<void>} A promise that resolves when the owner has been set.
   */
  async setOwner(company) {
    // This method is not supported at this time. I'm adding this so that if we decide to allow it in the future, it uses `composeJobPropertyUpdate` to propagate the change to `job_permissions`.
    throw new Error('Setting the job owner is not supported at this time');

    // Make sure the owner is a string
    if (typeof company !== 'string') throw new Error(`Invalid owner: ${company}`);

    // Create an update object and make the owner update
    const photoheightUpdate = composeJobPropertyUpdate('job_owner', company, { jobId: this.id, jobSharing: await this.getSharing() });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, photoheightUpdate);
  }

  /**
   * Get the job's project folder.
   * @returns {Promise<string>} The job's project folder.
   */
  async getProjectFolder() {
    return await this.#readDataOnce('project_folder');
  }

  /**
   * Listen for changes to the job's project folder.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's project folder changes.
   */
  onProjectFolderSnapshot(callback) {
    return this.#setUpListener('project_folder', callback);
  }

  /**
   * Set the job's project folder.
   * @param {string} projectFolder - The project folder to set for the job.
   * @returns {Promise<void>} A promise that resolves when the project folder has been set.
   */
  async setProjectFolder(projectFolder) {
    // Make sure the project folder is a string
    if (typeof projectFolder !== 'string') throw new Error(`Invalid project folder: ${projectFolder}`);

    const [jobOwner, jobSharing, jobProjectFolder] = await Promise.all([this.getOwner(), this.getSharing(), this.getProjectFolder()]);
    const currentProjectFolderPath = jobProjectFolder ? getFirebaseFolderPath(jobProjectFolder) : null;
    const ownerCurrentProjectFolderItems = currentProjectFolderPath
      ? (await this.database.ref(`photoheight/company_space/${jobOwner}/project_folders/${currentProjectFolderPath}`).once('value')).val()
      : null;

    // Create an update object and make the project folder update
    const photoheightUpdate = composeProjectFolderUpdate(projectFolder, {
      jobId: this.id,
      jobOwner,
      jobSharing,
      jobProjectFolder,
      ownerCurrentProjectFolderItems
    });

    // Perform the update
    return await this.#performUpdate('photoheight', photoheightUpdate);
  }

  /**
   * Get the job's project id.
   * @returns {Promise<string>} The job's project id.
   */
  async getProjectId() {
    return await this.#readDataOnce('project_id');
  }

  /**
   * Listen for changes to the job's project id.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's project id changes.
   * @returns {() => void} A function to remove the listener.
   */
  onProjectIdSnapshot(callback) {
    return this.#setUpListener('project_id', callback);
  }

  /**
   * Set the job's project id.
   * @param {string} projectId - The project id to set for the job.
   * @returns {Promise<void>} A promise that resolves when the project id has been set.
   */
  async setProjectId(projectId) {
    // Make sure the project id is a string
    if (typeof projectId !== 'string') throw new Error(`Invalid project id: ${projectId}`);

    // Create an update object
    const jobUpdate = { project_id: projectId };

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Get the job's status.
   * @returns {Promise<any>} The job's status.
   */
  async getStatus() {
    return await this.#readDataOnce('status');
  }

  /**
   * Listen for changes to the job's status.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's status changes.
   * @returns {() => void} A function to remove the listener.
   */
  onStatusSnapshot(callback) {
    return this.#setUpListener('status', callback);
  }

  /**
   * Set the job's status.
   * @param {{ published?: boolean, [key: string]: any }} status - The status object to set for the job.
   * @returns {Promise<void>} A promise that resolves when the status has been set.
   */
  async setStatus(status) {
    // Validate the function input
    if (status == null || typeof status !== 'object') throw new Error('Status must be an object');

    // Create an update object and make the status update
    const photoheightUpdate = composeJobStatusUpdate(status, { jobId: this.id, jobSharing: await this.getSharing() });

    // Perform the update
    return await this.#performUpdate('photoheight', photoheightUpdate);
  }

  /**
   * Get the job's "done" state.
   * @returns {Promise<any>} The job's "done" state.
   */
  async getDoneState() {
    return await this.#readDataOnce('done');
  }

  /**
   * Listen for changes to the job's "done" state.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's "done" state changes.
   * @returns {() => void} A function to remove the listener.
   */
  onDoneStateSnapshot(callback) {
    return this.#setUpListener('done', callback);
  }

  /**
   * Set the job's "done" state.
   * @param {boolean} done - The done state to set.
   * @returns {Promise<void>} A promise that resolves when the done state has been set.
   */
  async setDoneState(done) {
    if (typeof done !== 'boolean') throw new Error(`Invalid done state: ${done}`);

    // Create an update object and make the done state update
    const photoheightUpdate = composeJobDoneStateUpdate(done, { jobId: this.id, jobSharing: await this.getSharing() });

    // Perform the update
    return await this.#performUpdate('photoheight', photoheightUpdate);
  }

  /**
   * Get details about the job, including the name, model, and project folder, status, and published state.
   * @returns {Promise<import('./KatapultJobUtilities').JobDetails>} The job details.
   */
  async getDetails() {
    const [name, model, owner, projectFolder, projectId, status, done] = await Promise.all([
      this.getName(),
      this.getModel(),
      this.getOwner(),
      this.getProjectFolder(),
      this.getProjectId(),
      this.getStatus(),
      this.getDoneState()
    ]);
    return { name, model, owner, projectFolder, projectId, status, done };
  }

  /**
   * Listen for changes to the job's details.
   * @param {OnSnapshotCustomCallback<JobDetails>} callback - The callback function to be called when the job's details change.
   * @returns {() => void} A function to remove the listener.
   */
  onDetailsSnapshot(callback) {
    const details = { name: '', model: '', owner: '', projectFolder: '', projectId: undefined, status: undefined, done: undefined };
    const initialDetailsPromise = this.getDetails().then((initialDetails) => Object.assign(details, initialDetails));
    const setDetailsProperty = async (/** @type {string} */ property, /** @type {any} */ value) => {
      await initialDetailsPromise;
      details[property] = value;
      callback({ val: () => details });
    };

    const unsubscribers = [
      this.onNameSnapshot((snapshot) => setDetailsProperty('name', snapshot.val())),
      this.onModelSnapshot((snapshot) => setDetailsProperty('model', snapshot.val())),
      this.onOwnerSnapshot((snapshot) => setDetailsProperty('owner', snapshot.val())),
      this.onProjectFolderSnapshot((snapshot) => setDetailsProperty('projectFolder', snapshot.val())),
      this.onProjectIdSnapshot((snapshot) => setDetailsProperty('projectId', snapshot.val())),
      this.onStatusSnapshot((snapshot) => setDetailsProperty('status', snapshot.val())),
      this.onDoneStateSnapshot((snapshot) => setDetailsProperty('done', snapshot.val()))
    ];
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }

  /**
   * Get the job's metadata.
   * @returns {Promise<any>} The job's metadata.
   */
  async getMetadata() {
    return await this.#readDataOnce('metadata');
  }

  /**
   * Get the snapshot parent job id for the job.
   * @returns {Promise<?string>} The snapshot parent job id or null.
   */
  async getSnapshotParentJobId() {
    const parentJobId = await this.#readDataOnce('metadata/parent_job_id');
    return typeof parentJobId === 'string' ? parentJobId : null;
  }

  /**
   * Listen for changes to the job's metadata.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's metadata changes.
   * @returns {() => void} A function to remove the listener.
   */
  onMetadataSnapshot(callback) {
    return this.#setUpListener('metadata', callback);
  }

  /**
   * Get the job's parent job link data.
   * @deprecated Use direct job metadata or a dedicated field instead; this path may be removed in a future schema version.
   * @returns {Promise<{application_id?: (string|null), database?: (string|null), job_id?: (string|null), project_id?: (string|null)}|null>}
   *   The parent job link object, or null if not set.
   */
  async getParentJob() {
    return await this.#readDataOnce('parent_job');
  }

  /**
   * Listen for changes to the job's parent job link data.
   * @deprecated Use direct job metadata or a dedicated field instead; this path may be removed in a future schema version.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the parent job link changes.
   * @returns {() => void} A function to remove the listener.
   */
  onParentJobSnapshot(callback) {
    return this.#setUpListener('parent_job', callback);
  }

  /**
   * Update the job's metadata (modifying the attributes that are specified).
   * @param {Record<string,any>} metadata - An object mapping metadata attributes to their new values.
   * @returns {Promise<void>} A promise that resolves when the metadata has been updated.
   */
  async updateMetadata(metadata) {
    // Validate the function input
    if (metadata == null || typeof metadata !== 'object') throw new Error('Metadata must be an object');

    // Get the current job sharing for the update
    const jobSharing = await this.getSharing();

    // Create an update object and begin updating the metadata
    const photoheightUpdate = {};

    // Compose an update for each entry in the metadata object
    for (const [attribute, value] of Object.entries(metadata)) {
      const partialUpdate = composeMetadataAttributeUpdate(attribute, value, { jobId: this.id, jobSharing });
      Object.assign(photoheightUpdate, partialUpdate);
    }

    // Perform the update
    return await this.#performUpdate('photoheight', photoheightUpdate);
  }

  /**
   * Set a metadata attribute on the job.
   * @param {string} attribute - The attribute to set.
   * @param {any} value - The value to set the attribute to.
   * @returns {Promise<void>} A promise that resolves when the attribute has been set.
   */
  async setMetadataAttribute(attribute, value) {
    // Create an update object and make the attribute update
    const photoheightUpdate = composeMetadataAttributeUpdate(attribute, value, { jobId: this.id, jobSharing: await this.getSharing() });

    // Perform the update
    return await this.#performUpdate('photoheight', photoheightUpdate);
  }

  /**
   * Get the job's map styles.
   * @returns {Promise<any>} The job's map styles.
   */
  async getJobStyles() {
    return await this.#readDataOnce('map_styles');
  }

  /**
   * Listen for changes to the job's map styles.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's map styles change.
   * @returns {() => void} A function to remove the listener.
   */
  onJobStylesSnapshot(callback) {
    return this.#setUpListener('map_styles', callback);
  }

  /**
   * Get the job's nodes.
   * @returns {Promise<any>} The job's nodes.
   */
  async getNodes() {
    return await this.#readDataOnce('nodes');
  }

  /**
   * Listen for changes to the job's nodes.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's nodes change.
   * @returns {() => void} A function to remove the listener.
   */
  onNodesSnapshot(callback) {
    return this.#setUpListener('nodes', callback);
  }

  /**
   * Determine whether the job has at least one node.
   * @returns {Promise<boolean>} Whether the job has any nodes.
   */
  async hasNodes() {
    return await this.reference
      .child('nodes')
      .limitToFirst(1)
      .once('value')
      .then((snapshot) => snapshot.exists());
  }

  /**
   * Get a specific node from the job.
   * @param {string} nodeId - The id (key) of the node.
   * @returns {Promise<any>} The node.
   */
  async getNodeById(nodeId) {
    return await this.#readDataOnce(`nodes/${nodeId}`);
  }

  /**
   * Get the connections that have the specified node as an endpoint.
   * @param {string} nodeId - The id (key) of the node.
   * @returns {Promise<any>} The connections that have the specified node as an endpoint.
   */
  async getNodeConnections(nodeId) {
    const connections = await this.getConnections();
    const nodeConnections = Object.fromEntries(
      Object.entries(connections).filter(([, { node_id_1, node_id_2 }]) => node_id_1 === nodeId || node_id_2 === nodeId)
    );
    return nodeConnections;
  }

  /**
   * Get the compatible units for a specific node in the job.
   * @param {string} nodeId - The id (key) of the node.
   * @returns {Promise<any>} The compatible units for the node.
   */
  async getCompatibleUnitsByNodeId(nodeId) {
    return await this.#readDataOnce(`compatible_units/${nodeId}`);
  }

  /**
   * Create a node in the job.
   * @param {[number, number]} location - The latitude and longitude of the node.
   * @param {Partial<CreateNodeData>} [nodeData] - Optional data to set on the node.
   * @returns {Promise<string>} The id (key) of the created node.
   */
  async createNode(location, nodeData) {
    // Node data (if provided) must be an object
    if (nodeData != null && typeof nodeData !== 'object') throw new Error('Node data must be an object');
    // Get the latitude and longitude
    const { latitude, longitude } = validateLocationInput(location);

    // Create the node
    const { _key, _create_method, ...otherNodeData } = nodeData ?? {};
    const _created = await getModificationTimestamp(this.firebase, _create_method);
    const node = { latitude, longitude, _created, ...otherNodeData };

    // Get the job's styles
    const jobStyles = await this.getJobStyles();

    // Get an ID for the node
    const nodeId = _key ?? String(this.reference.push().key);
    // Create an update object and begin creating the node
    const jobUpdate = composeNodeCreationUpdate(nodeId, node, { jobStyles });

    // Perform the update
    await this.#performUpdate(this.#jobPath, jobUpdate);

    // Return the node id
    return nodeId;
  }

  /**
   * Delete a node from the job. Any connections that reference the node (and their sections) will also be deleted. Any photos associated
   * with the node and its connections (and their sections) will be unassociated.
   * @param {string} nodeId - The id (key) of the node.
   * @returns {Promise<void>} A promise that resolves when the node has been deleted.
   */
  async deleteNode(nodeId) {
    // Get the node before deleting it
    const node = await this.getNodeById(nodeId);
    // If the node doesn't exist, do nothing
    if (node == null) return;

    // Get the current job data for the update
    const [jobConnections, jobPhotos] = await Promise.all([this.getConnections(), this.getPhotos()]);

    // Create an update object and begin deleting the node
    const jobsUpdate = composeNodeDeleteUpdate(nodeId, node, { jobId: this.id, jobConnections, jobPhotos });

    // Perform the update
    return await this.#performUpdate('photoheight/jobs', jobsUpdate);
  }

  /**
   * Set the location of a node in the job.
   * @param {string} nodeId - The id (key) of the node.
   * @param {[number, number]} location - The latitude and longitude of the node.
   * @param {object} [options] - Options for the location update.
   * @param {boolean} [options.moveAttachedDownGuys=true] - Whether to move attached down guys when updating the node location.
   * @returns {Promise<void>} A promise that resolves when the location has been set.
   */
  async setNodeLocation(nodeId, location, options = { moveAttachedDownGuys: true }) {
    // Get the latitude and longitude
    const { latitude, longitude } = validateLocationInput(location);

    // Get the node
    const node = await this.getNodeById(nodeId);
    // If the node doesn't exist, throw an error
    if (node == null) throw new Error(`Node not found: ${nodeId}`);

    // Get current job connections and nodes for the update
    const [jobConnections, jobNodes] = await Promise.all([this.getConnections(), this.getNodes()]);

    // TODO (2024-04-15): App geohash?

    // Create an update object and make the location update
    const jobUpdate = composeNodeLocationUpdate(nodeId, [latitude, longitude], { jobConnections, jobNodes });

    if (options.moveAttachedDownGuys) {
      const nodeIsEndpoint = (connection) => connection.node_id_1 === nodeId || connection.node_id_2 === nodeId;
      const isAnchorType = (connection) =>
        PickAnAttribute(connection.attributes, 'connection_type') == 'down guy' ||
        PickAnAttribute(connection.attributes, 'connection_type') == 'pushbrace';
      // Get a list of all connections that will be modified along with the node
      const connectionsToModify = Object.entries(jobConnections ?? {}).filter(
        ([, connection]) => nodeIsEndpoint(connection) && isAnchorType(connection)
      );
      const oldLatLngForNode = new GoogleGeometry.maps.LatLng(jobNodes[nodeId].latitude, jobNodes[nodeId].longitude);
      const newLatLngForNode = new GoogleGeometry.maps.LatLng(latitude, longitude);
      for (const connection of connectionsToModify) {
        const node1Type = PickAnAttribute(jobNodes[connection[1].node_id_1].attributes, 'node_type');
        const applicableNodeTypes = ['pushbrace', 'existing anchor', 'new anchor'];
        const anchorNodeId = applicableNodeTypes.includes(node1Type) ? connection[1].node_id_1 : connection[1].node_id_2;
        // Get the existing anchor location
        const oldAnchorLatLng = new GoogleGeometry.maps.LatLng(jobNodes[anchorNodeId].latitude, jobNodes[anchorNodeId].longitude);
        // Move the anchor node and connection
        const offsetDist = GoogleGeometry.maps.geometry.spherical.computeDistanceBetween(oldAnchorLatLng, oldLatLngForNode);
        const offsetDir = GoogleGeometry.maps.geometry.spherical.computeHeading(oldLatLngForNode, oldAnchorLatLng);

        // Update the anchor position
        const newAnchorLatLng = GoogleGeometry.maps.geometry.spherical.computeOffset(newLatLngForNode, offsetDist, offsetDir);

        const anchorUpdate = composeNodeLocationUpdate(anchorNodeId, [newAnchorLatLng.lat(), newAnchorLatLng.lng()], {
          jobConnections,
          jobNodes
        });

        Object.assign(jobUpdate, anchorUpdate);
      }
    }

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Add an attribute with a specified value to a node.
   * @param {string} nodeId - The id (key) of the node.
   * @param {string} attribute - The name of the attribute to set.
   * @param {any} value - The value to set the attribute to.
   * @returns {Promise<void>} A promise that resolves when the attribute has been set.
   */
  async addNodeAttributeValue(nodeId, attribute, value) {
    // Get supplemental data from the job
    const [node, jobStyles] = await Promise.all([this.getNodeById(nodeId), this.getJobStyles()]);
    // If the node doesn't exist, throw an error
    if (node == null) throw new Error(`Node not found: ${nodeId}`);

    // Get a key for the new attribute instance
    const instanceKey = String(this.reference.push().key);
    const attributeChanges = { [attribute]: { [instanceKey]: value } };

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('node', nodeId, node, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Update an attribute on a node.
   * @param {string} nodeId - The id (key) of the node.
   * @param {string} attribute - The name of the attribute to update.
   * @param {Record<string, any>|null} values - The new values for the attribute.
   * @param {import('./KatapultJobUtilities').UpdateAttributesOptions} [options] - Options for the update.
   */
  async updateNodeAttribute(nodeId, attribute, values, options) {
    const { replaceExisting = false } = options || {};
    // Get supplemental data from the job
    const [node, jobStyles] = await Promise.all([this.getNodeById(nodeId), this.getJobStyles()]);
    // If the node doesn't exist, throw an error
    if (node == null) throw new Error(`Node not found: ${nodeId}`);

    // If we're replacing existing values, set any existing value instances to null
    const attributes = { [attribute]: values };
    const attributeChanges = replaceExisting ? createReplacingItemAttributesMutation(node.attributes, attributes) : attributes;

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('node', nodeId, node, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Add multiple attributes to a node.
   * @param {string} nodeId - The id (key) of the node.
   * @param {[string, any][]} attributeValueEntries - An array of attribute-value pairs to add to the node.
   * @returns {Promise<void>} A promise that resolves when the attributes have been added.
   */
  async addNodeAttributeValues(nodeId, attributeValueEntries) {
    // Get supplemental data from the job
    const [node, jobStyles] = await Promise.all([this.getNodeById(nodeId), this.getJobStyles()]);
    // If the node doesn't exist, throw an error
    if (node == null) throw new Error(`Node not found: ${nodeId}`);

    // Create the attributes object
    const attributeChanges = attributeValueEntries.reduce(
      (attributes, [attribute, value]) => ({
        ...attributes,
        [attribute]: { ...attributes[attribute], [String(this.reference.push().key)]: value }
      }),
      {}
    );

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('node', nodeId, node, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Update multiple attributes on a node.
   * @param {string} nodeId - The id (key) of the node.
   * @param {import('./KatapultJobUtilities').ItemAttributesMutation} attributes - An object mapping attribute names to objects that map
   * instance keys to values.
   * @param {import('./KatapultJobUtilities').UpdateAttributesOptions} [options] - Options for the update.
   * @returns {Promise<void>} A promise that resolves when the attributes have been updated.
   */
  async updateNodeAttributes(nodeId, attributes, options) {
    const { replaceExisting = false } = options || {};
    // Get supplemental data from the job
    const [node, jobStyles] = await Promise.all([this.getNodeById(nodeId), this.getJobStyles()]);
    // If the node doesn't exist, throw an error
    if (node == null) throw new Error(`Node not found: ${nodeId}`);

    // If we're replacing existing values, set any existing value instances to null
    const attributeChanges = replaceExisting ? createReplacingItemAttributesMutation(node.attributes, attributes) : attributes;

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('node', nodeId, node, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Remove one or more attributes from a node.
   * @param {string} nodeId - The id (key) of the node.
   * @param {string|string[]} [attributeOrAttributesToRemove] - An array of attribute names to remove from the node. If not provided, all
   * attributes will be removed.
   * @returns {Promise<void>} A promise that resolves when the attributes have been removed.
   */
  async removeNodeAttributes(nodeId, attributeOrAttributesToRemove) {
    // Get supplemental data from the job
    const [node, jobStyles] = await Promise.all([this.getNodeById(nodeId), this.getJobStyles()]);
    // If the node doesn't exist, throw an error
    if (node == null) throw new Error(`Node not found: ${nodeId}`);

    // Get the attributes to remove (if not provided, remove all attributes)
    const attributesToRemove = Array.isArray(attributeOrAttributesToRemove)
      ? attributeOrAttributesToRemove
      : attributeOrAttributesToRemove != null
        ? [attributeOrAttributesToRemove]
        : Object.keys(node.attributes ?? {});
    const attributeChanges = Object.fromEntries(attributesToRemove.map((attribute) => [attribute, null]));

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('node', nodeId, node, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Update ROW record file entries for a pole.
   * @param {string} nodeId - The id (key) of the pole node.
   * @param {Record<string, any>} rowRecordUpdate - Partial update for the node's ROW record entries.
   * @returns {Promise<void>} A promise that resolves when the ROW records have been updated.
   */
  async updatePoleRowRecords(nodeId, rowRecordUpdate) {
    if (nodeId == null || typeof nodeId !== 'string') throw new Error('Missing required parameter: nodeId');
    if (rowRecordUpdate == null || typeof rowRecordUpdate !== 'object') throw new Error('Missing required parameter: rowRecordUpdate');

    return await this.reference.child(`files/poles/${nodeId}/ROW_record`).update(rowRecordUpdate);
  }

  /**
   * Get the job's connections.
   * @returns {Promise<any>} The job's connections.
   */
  async getConnections() {
    return await this.#readDataOnce('connections');
  }

  /**
   * Listen for changes to the job's connections.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's connections change.
   * @returns {() => void} A function to remove the listener.
   */
  onConnectionsSnapshot(callback) {
    return this.#setUpListener('connections', callback);
  }

  /**
   * Get a specific connection from the job.
   * @param {string} connectionId - The id (key) of the connection.
   * @returns {Promise<any>} The connection.
   */
  async getConnectionById(connectionId) {
    return await this.#readDataOnce(`connections/${connectionId}`);
  }

  /**
   * Get the nodes that are connected by a connection.
   * @param {string} connectionId - The id (key) of the connection.
   * @returns {Promise<Record<string, any>|null>} An object mapping node ids to nodes, or null if the connection doesn't exist.
   */
  async getConnectionNodes(connectionId) {
    const connection = await this.getConnectionById(connectionId);
    if (connection == null) return null;

    const { node_id_1: nodeId1, node_id_2: nodeId2 } = connection;
    const [node1, node2] = await Promise.all([this.getNodeById(nodeId1), this.getNodeById(nodeId2)]);

    return { [nodeId1]: node1, [nodeId2]: node2 };
  }

  /**
   * Create a connection between two nodes in the job.
   * @param {string} nodeId1 - The id (key) of the first node.
   * @param {string} nodeId2 - The id (key) of the second node.
   * @param {Partial<CreateConnectionData>} [connectionData] - Optional data to set on the connection.
   * @returns {Promise<string>} The id (key) of the created connection.
   */
  async createConnection(nodeId1, nodeId2, connectionData) {
    // Validate the function input
    if (typeof nodeId1 !== 'string' || typeof nodeId2 !== 'string') throw new Error('Node ids must be strings');
    if (nodeId1 === nodeId2) throw new Error('Node ids must be different');
    if (connectionData != null && typeof connectionData !== 'object') throw new Error('Connection data must be an object');

    // Get the nodes and job styles
    const [node1, node2, jobStyles] = await Promise.all([this.getNodeById(nodeId1), this.getNodeById(nodeId2), this.getJobStyles()]);
    // If either node doesn't exist, throw an error
    if (node1 == null) throw new Error(`Node not found: ${nodeId1}`);
    if (node2 == null) throw new Error(`Node not found: ${nodeId2}`);

    // Create the connection
    const { _key, _create_method, ...connectionDataWithoutKey } = connectionData ?? {};
    const _created = await getModificationTimestamp(this.firebase, _create_method);
    const connection = { node_id_1: nodeId1, node_id_2: nodeId2, _created, ...connectionDataWithoutKey };

    // Create the connection on the job
    const connectionId = _key ?? String(this.reference.push().key);
    const geohashUpdate = {};
    // geoPath defaults to 'geohash/' inside GeofireTools._setDefaultOptions when not provided
    GeofireTools.setGeohash('connections', connection, connectionId, jobStyles, geohashUpdate, {
      location1: [node1.latitude, node1.longitude],
      location2: [node2.latitude, node2.longitude],
      nId1: nodeId1,
      nId2: nodeId2,
      google: GoogleGeometry
    });
    // Create an update object and begin creating the connection
    // GeofireTools.setGeohash already includes style fields, so we skip composeItemGeoStyleUpdate here.
    const jobUpdate = {
      [`connections/${connectionId}`]: connection,
      ...geohashUpdate
      // TODO (2024-04-17): Photo association, auto-creation of section geohashes, etc.
    };

    // Perform the update
    await this.#performUpdate(this.#jobPath, jobUpdate);
    return connectionId;
  }

  /**
   * Set connection breakpoint geometry without recreating the connection.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {Array<[number, number]>|null} breakpoints - New breakpoints. Use [] or null to clear.
   * @returns {Promise<void>} A promise that resolves when the connection has been updated.
   */
  async setConnectionBreakpoints(connectionId, breakpoints) {
    // Validate inputs
    if (typeof connectionId !== 'string') throw new Error('Connection id must be a string');
    if (breakpoints != null && !Array.isArray(breakpoints)) throw new Error('Breakpoints must be an array or null');
    const normalizedBreakpoints = breakpoints ?? [];

    // Fetch current connection and endpoint nodes
    const connection = await this.getConnectionById(connectionId);
    if (connection == null) throw new Error(`Connection not found: ${connectionId}`);

    const [node1, node2, jobStyles] = await Promise.all([
      this.getNodeById(connection.node_id_1),
      this.getNodeById(connection.node_id_2),
      this.getJobStyles()
    ]);
    if (node1 == null || node2 == null) throw new Error(`Connection endpoints not found: ${connectionId}`);

    const updatedConnection = { ...connection, breakpoints: normalizedBreakpoints };

    // Move every section onto the connection's new path in the same update
    const updatedConnectionPathPoints = [[node1.latitude, node1.longitude], ...normalizedBreakpoints, [node2.latitude, node2.longitude]];
    const sectionsUpdate = composeSectionsSnappedToConnectionPathUpdate(connectionId, connection.sections, updatedConnectionPathPoints);

    const geohashUpdate = {};
    GeofireTools.setGeohash('connections', updatedConnection, connectionId, jobStyles, geohashUpdate, {
      location1: [node1.latitude, node1.longitude],
      location2: [node2.latitude, node2.longitude],
      nId1: connection.node_id_1,
      nId2: connection.node_id_2,
      google: GoogleGeometry
    });
    return await this.reference.update({
      [`connections/${connectionId}/breakpoints`]: normalizedBreakpoints,
      ...geohashUpdate,
      ...sectionsUpdate
    });
  }

  /**
   * Delete a connection from the job. Any sections on the connection will also be deleted. Any photos associated with the connection or its
   * sections will be unassociated.
   * @param {string} connectionId - The id (key) of the connection.
   * @returns {Promise<void>} A promise that resolves when the connection has been deleted.
   */
  async deleteConnection(connectionId) {
    // Get the connection before deleting it
    const connection = await this.getConnectionById(connectionId);
    // If the connection doesn't exist, do nothing
    if (connection == null) return;

    // Create an update object and begin deleting the connection
    const jobUpdate = composeConnectionDeleteUpdate(connectionId, connection, { jobPhotos: await this.getPhotos() });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Add an attribute with a specified value to a connection.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string} attribute - The name of the attribute to set.
   * @param {any} value - The value to set the attribute to.
   * @returns {Promise<void>} A promise that resolves when the attribute has been set.
   */
  async addConnectionAttributeValue(connectionId, attribute, value) {
    // Get supplemental data from the job
    const [connection, jobStyles] = await Promise.all([this.getConnectionById(connectionId), this.getJobStyles()]);
    // If the connection doesn't exist, throw an error
    if (connection == null) throw new Error(`Connection not found: ${connectionId}`);

    // Get a key for the new attribute instance
    const instanceKey = String(this.reference.push().key);
    const attributeChanges = { [attribute]: { [instanceKey]: value } };

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('connection', connectionId, connection, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Update an attribute on a connection.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string} attribute - The name of the attribute to update.
   * @param {Record<string, any>|null} values - The new values for the attribute.
   * @param {import('./KatapultJobUtilities').UpdateAttributesOptions} [options] - Options for the update.
   * @returns {Promise<void>} A promise that resolves when the attribute has been updated.
   */
  async updateConnectionAttribute(connectionId, attribute, values, options) {
    const { replaceExisting = false } = options || {};
    // Get supplemental data from the job
    const [connection, jobStyles] = await Promise.all([this.getConnectionById(connectionId), this.getJobStyles()]);
    // If the connection doesn't exist, throw an error
    if (connection == null) throw new Error(`Connection not found: ${connectionId}`);

    // If we're replacing existing values, set any existing value instances to null
    const attributes = { [attribute]: values };
    const attributeChanges = replaceExisting ? createReplacingItemAttributesMutation(connection.attributes, attributes) : attributes;

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('connection', connectionId, connection, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Add multiple attributes to a connection.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {[string, any][]} attributeValueEntries - An array of attribute-value pairs to add to the connection.
   * @returns {Promise<void>} A promise that resolves when the attributes have been added.
   */
  async addConnectionAttributeValues(connectionId, attributeValueEntries) {
    // Get supplemental data from the job
    const [connection, jobStyles] = await Promise.all([this.getConnectionById(connectionId), this.getJobStyles()]);
    // If the connection doesn't exist, throw an error
    if (connection == null) throw new Error(`Connection not found: ${connectionId}`);

    // Create the attributes object
    const attributeChanges = attributeValueEntries.reduce(
      (attributes, [attribute, value]) => ({
        ...attributes,
        [attribute]: { ...attributes[attribute], [String(this.reference.push().key)]: value }
      }),
      {}
    );

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('connection', connectionId, connection, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Update multiple attributes on a connection.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {import('./KatapultJobUtilities').ItemAttributesMutation} attributes - An object mapping attribute names to objects that map
   * instance keys to values.
   * @param {import('./KatapultJobUtilities').UpdateAttributesOptions} [options] - Options for the update.
   * @returns {Promise<void>} A promise that resolves when the attributes have been updated.
   */
  async updateConnectionAttributes(connectionId, attributes, options) {
    const { replaceExisting = false } = options || {};
    // Get supplemental data from the job
    const [connection, jobStyles] = await Promise.all([this.getConnectionById(connectionId), this.getJobStyles()]);
    // If the connection doesn't exist, throw an error
    if (connection == null) throw new Error(`Connection not found: ${connectionId}`);

    // If we're replacing existing values, set any existing value instances to null
    const attributeChanges = replaceExisting ? createReplacingItemAttributesMutation(connection.attributes, attributes) : attributes;

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('connection', connectionId, connection, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Remove one or more attributes from a connection.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string|string[]} [attributeOrAttributesToRemove] - An array of attribute names to remove from the connection. If not provided,
   * all attributes will be removed.
   * @returns {Promise<void>} A promise that resolves when the attributes have been removed.
   */
  async removeConnectionAttributes(connectionId, attributeOrAttributesToRemove) {
    // Get supplemental data from the job
    const [connection, jobStyles] = await Promise.all([this.getConnectionById(connectionId), this.getJobStyles()]);
    // If the connection doesn't exist, throw an error
    if (connection == null) throw new Error(`Connection not found: ${connectionId}`);

    // Get the attributes to remove (if not provided, remove all attributes)
    const attributesToRemove = Array.isArray(attributeOrAttributesToRemove)
      ? attributeOrAttributesToRemove
      : attributeOrAttributesToRemove != null
        ? [attributeOrAttributesToRemove]
        : Object.keys(connection.attributes ?? {});
    const attributeChanges = Object.fromEntries(attributesToRemove.map((attribute) => [attribute, null]));

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('connection', connectionId, connection, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Get the sections on a connection.
   * @param {string} connectionId - The id (key) of the connection.
   * @returns {Promise<any>} The job's sections.
   */
  async getSections(connectionId) {
    return await this.#readDataOnce(`connections/${connectionId}/sections`);
  }

  /**
   * Listen for changes to the sections on a connection.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the sections change.
   * @returns {() => void} A function to remove the listener.
   */
  onSectionsSnapshot(connectionId, callback) {
    return this.#setUpListener(`connections/${connectionId}/sections`, callback);
  }

  /**
   * Get a specific section from a connection in the job.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string} sectionKey - The key of the section.
   * @returns {Promise<any>} The section.
   */
  async getSectionByIds(connectionId, sectionKey) {
    return this.#readDataOnce(`connections/${connectionId}/sections/${sectionKey}`);
  }

  /**
   * Create a section on a connection.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {[number, number]} location - The latitude and longitude of the section (will be snapped to the connection).
   * @param {Partial<CreateSectionData>} [sectionData] - Optional data to set on the section.
   * @returns {Promise<string>} The id (key) of the created section.
   */
  async createSection(connectionId, location, sectionData) {
    // Section data (if provided) must be an object
    if (sectionData != null && typeof sectionData !== 'object') throw new Error('Section data must be an object');

    // Get the connection and job styles
    const [connection, jobStyles] = await Promise.all([this.getConnectionById(connectionId), this.getJobStyles()]);

    // Snap the location to the connection's line
    if (connection == null) throw new Error(`Connection not found: ${connectionId}`);
    const [node1, node2] = await Promise.all([this.getNodeById(connection.node_id_1), this.getNodeById(connection.node_id_2)]);
    if (node1 == null) throw new Error(`Node not found: ${connection.node_id_1}`);
    if (node2 == null) throw new Error(`Node not found: ${connection.node_id_2}`);

    // Get the latitude and longitude
    const { latitude, longitude } = validateAndSnapLocationInput(location, node1, node2, connection.breakpoints);

    // Create the section
    const { _key, _create_method, ...sectionDataWithoutKey } = sectionData ?? {};
    const _created = await getModificationTimestamp(this.firebase, _create_method);
    const section = { latitude, longitude, _created, ...sectionDataWithoutKey };

    // Get an ID for the section
    const sectionKey = _key ?? String(this.reference.push().key);
    // Create an update object and begin creating the section
    const jobUpdate = {
      [`connections/${connectionId}/sections/${sectionKey}`]: section,
      ...composeGeohashLocationUpdate(`${connectionId}:${sectionKey}`, [latitude, longitude]),
      ...composeItemGeoStyleUpdate('section', `${connectionId}:${sectionKey}`, section, { jobStyles }),
      [`geohash/${connectionId}:${sectionKey}/t`]: 's'
      // TODO (2024-04-17): This almost certainly should do more than just these two things (photo association? idk)
    };
    await this.#performUpdate(this.#jobPath, jobUpdate);

    // Create the section on the job
    return sectionKey;
  }

  /**
   * Delete a section from the job. Any photos associated with the section will be unassociated.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string} sectionKey - The key of the section.
   * @returns {Promise<void>} A promise that resolves when the section has been deleted.
   */
  async deleteSection(connectionId, sectionKey) {
    // Get the section before deleting it
    const section = await this.getSectionByIds(connectionId, sectionKey);
    // If the section doesn't exist, do nothing
    if (section == null) return;

    // Create an update object and begin deleting the section
    const jobUpdate = composeSectionDeleteUpdate(connectionId, sectionKey, section, { jobPhotos: await this.getPhotos() });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Set the location of a section in the job.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string} sectionKey - The key of the section.
   * @param {[number, number]} location - The latitude and longitude of the section (will be snapped to the connection).
   * @returns {Promise<void>} A promise that resolves when the location has been set.
   */
  async setSectionLocation(connectionId, sectionKey, location) {
    // Get the section
    const section = await this.getSectionByIds(connectionId, sectionKey);
    // If the section doesn't exist, throw an error
    if (section == null) throw new Error(`Section not found: ${sectionKey}`);

    // Snap the location to the connection's line
    const connection = await this.getConnectionById(connectionId);
    if (connection == null) throw new Error(`Connection not found: ${connectionId}`);
    const [node1, node2] = await Promise.all([this.getNodeById(connection.node_id_1), this.getNodeById(connection.node_id_2)]);
    if (node1 == null) throw new Error(`Node not found: ${connection.node_id_1}`);
    if (node2 == null) throw new Error(`Node not found: ${connection.node_id_2}`);

    // Get the latitude and longitude
    const { latitude, longitude } = validateAndSnapLocationInput(location, node1, node2, connection.breakpoints);

    // Create an update object and make the location update
    const jobUpdate = composeSectionLocationUpdate(connectionId, sectionKey, [latitude, longitude]);

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Move every section on a connection onto the closest point of the connection's current path.
   * @param {string} connectionId - The id (key) of the connection.
   * @returns {Promise<void>} A promise that resolves when the section locations have been set.
   */
  async snapSectionsToConnectionPath(connectionId) {
    // Get the connection and skip it when it has no sections to move
    const connection = await this.getConnectionById(connectionId);
    if (connection == null) throw new Error(`Connection not found: ${connectionId}`);
    if (connection.sections == null) return;

    // Get the end nodes of the connection
    const [node1, node2] = await Promise.all([this.getNodeById(connection.node_id_1), this.getNodeById(connection.node_id_2)]);
    if (node1 == null) throw new Error(`Node not found: ${connection.node_id_1}`);
    if (node2 == null) throw new Error(`Node not found: ${connection.node_id_2}`);

    // Snap each section onto the connection's whole path
    const connectionPathPoints = [[node1.latitude, node1.longitude], ...(connection.breakpoints ?? []), [node2.latitude, node2.longitude]];
    const jobUpdate = composeSectionsSnappedToConnectionPathUpdate(connectionId, connection.sections, connectionPathPoints);

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Add an attribute with a specified value to a section.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string} sectionKey - The key of the section.
   * @param {string} attribute - The name of the attribute to set.
   * @param {any} value - The value to set the attribute to.
   * @returns {Promise<void>} A promise that resolves when the attribute has been set.
   */
  async addSectionAttributeValue(connectionId, sectionKey, attribute, value) {
    // Get supplemental data from the job
    const [section, jobStyles] = await Promise.all([this.getSectionByIds(connectionId, sectionKey), this.getJobStyles()]);
    // If the section doesn't exist, throw an error
    if (section == null) throw new Error(`Section not found: ${sectionKey}`);

    // Get a key for the new attribute instance
    const instanceKey = String(this.reference.push().key);

    // Get the section ID
    const sectionId = `${connectionId}:${sectionKey}`;
    const attributeChanges = { [attribute]: { [instanceKey]: value } };

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('section', sectionId, section, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Update an attribute on a section.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string} sectionKey - The key of the section.
   * @param {string} attribute - The name of the attribute to update.
   * @param {Record<string, any>|null} values - The new values for the attribute.
   * @param {import('./KatapultJobUtilities').UpdateAttributesOptions} [options] - Options for the update.
   * @returns {Promise<void>} A promise that resolves when the attribute has been updated.
   */
  async updateSectionAttribute(connectionId, sectionKey, attribute, values, options) {
    const { replaceExisting = false } = options || {};
    // Get supplemental data from the job
    const [section, jobStyles] = await Promise.all([this.getSectionByIds(connectionId, sectionKey), this.getJobStyles()]);
    // If the section doesn't exist, throw an error
    if (section == null) throw new Error(`Section not found: ${sectionKey}`);

    // If we're replacing existing values, set any existing value instances to null
    const attributes = { [attribute]: values };
    const attributeChanges = replaceExisting ? createReplacingItemAttributesMutation(section.multi_attributes, attributes) : attributes;

    // Get the section ID
    const sectionId = `${connectionId}:${sectionKey}`;

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('section', sectionId, section, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Add multiple attributes to a section.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string} sectionKey - The key of the section.
   * @param {[string, any][]} attributeValueEntries - An array of attribute-value pairs to add to the section.
   * @returns {Promise<void>} A promise that resolves when the attributes have been added.
   */
  async addSectionAttributeValues(connectionId, sectionKey, attributeValueEntries) {
    // Get supplemental data from the job
    const [section, jobStyles] = await Promise.all([this.getSectionByIds(connectionId, sectionKey), this.getJobStyles()]);
    // If the section doesn't exist, throw an error
    if (section == null) throw new Error(`Section not found: ${sectionKey}`);

    // Get the section ID
    const sectionId = `${connectionId}:${sectionKey}`;

    // Create the attributes object
    const attributeChanges = attributeValueEntries.reduce(
      (attributes, [attribute, value]) => ({
        ...attributes,
        [attribute]: { ...attributes[attribute], [String(this.reference.push().key)]: value }
      }),
      {}
    );

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('section', sectionId, section, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Update multiple attributes on a section.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string} sectionKey - The key of the section.
   * @param {import('./KatapultJobUtilities').ItemAttributesMutation} attributes - An object mapping attribute names to objects that map
   * instance keys to values.
   * @param {import('./KatapultJobUtilities').UpdateAttributesOptions} [options] - Options for the update.
   * @returns {Promise<void>} A promise that resolves when the attributes have been updated.
   */
  async updateSectionAttributes(connectionId, sectionKey, attributes, options) {
    const { replaceExisting = false } = options || {};
    // Get supplemental data from the job
    const [section, jobStyles] = await Promise.all([this.getSectionByIds(connectionId, sectionKey), this.getJobStyles()]);
    // If the section doesn't exist, throw an error
    if (section == null) throw new Error(`Section not found: ${sectionKey}`);

    // Get the section ID
    const sectionId = `${connectionId}:${sectionKey}`;

    // If we're replacing existing values, set any existing value instances to null
    const attributeChanges = replaceExisting ? createReplacingItemAttributesMutation(section.multi_attributes, attributes) : attributes;

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('section', sectionId, section, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Remove one or more attributes from a section.
   * @param {string} connectionId - The id (key) of the connection.
   * @param {string} sectionKey - The key of the section.
   * @param {string|string[]} [attributeOrAttributesToRemove] - An array of attribute names to remove from the section. If not provided, all
   * attributes will be removed.
   * @returns {Promise<void>} A promise that resolves when the attributes have been removed.
   */
  async removeSectionAttributes(connectionId, sectionKey, attributeOrAttributesToRemove) {
    // Get supplemental data from the job
    const [section, jobStyles] = await Promise.all([this.getSectionByIds(connectionId, sectionKey), this.getJobStyles()]);
    // If the section doesn't exist, there's nothing to remove
    if (section == null) return;

    // Get the section ID
    const sectionId = `${connectionId}:${sectionKey}`;

    // Get the attributes to remove (if not provided, remove all attributes)
    const attributesToRemove = Array.isArray(attributeOrAttributesToRemove)
      ? attributeOrAttributesToRemove
      : attributeOrAttributesToRemove != null
        ? [attributeOrAttributesToRemove]
        : Object.keys(section.multi_attributes ?? {});
    const attributeChanges = Object.fromEntries(attributesToRemove.map((attribute) => [attribute, null]));

    // Create an update object
    const jobUpdate = composeItemAttributesUpdate('section', sectionId, section, attributeChanges, { jobStyles });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Update attributes on many nodes and sections in a single write. Items that no longer exist in the job are skipped rather than
   * recreated, and are returned so the caller can report them.
   * @param {Array<{ itemType: 'node'|'section', itemId: string, attributes: import('./KatapultJobUtilities').ItemAttributesMutation }>} itemAttributeChanges -
   * The attribute changes to make, one entry per item. A section's itemId is `${connectionId}:${sectionKey}`.
   * @param {import('./KatapultJobUtilities').UpdateAttributesOptions} [options] - Options for the update, applied to every item.
   * @returns {Promise<{ missingItems: Array<{ itemType: 'node'|'section', itemId: string }> }>} The items that were not found in the job.
   */
  async updateItemsAttributes(itemAttributeChanges, options) {
    const { replaceExisting = false } = options || {};
    const hasNodes = itemAttributeChanges.some(({ itemType }) => itemType === 'node');
    const hasSections = itemAttributeChanges.some(({ itemType }) => itemType === 'section');

    // Get supplemental data from the job, only reading the item collections that are being changed
    const [nodes, connections, jobStyles] = await Promise.all([
      hasNodes ? this.getNodes() : null,
      hasSections ? this.getConnections() : null,
      this.getJobStyles()
    ]);

    const jobUpdate = {};
    const missingItems = [];
    for (const { itemType, itemId, attributes } of itemAttributeChanges) {
      let item;
      if (itemType === 'node') {
        item = nodes?.[itemId];
      } else if (itemType === 'section') {
        const [connectionId, sectionKey] = itemId.split(':');
        item = connections?.[connectionId]?.sections?.[sectionKey];
      } else {
        throw new Error(`Unsupported item type: ${itemType}`);
      }

      // Skip items that don't exist so the update doesn't create partial items
      if (item == null) {
        missingItems.push({ itemType, itemId });
        continue;
      }

      // If we're replacing existing values, set any existing value instances to null
      const existingAttributes = itemType === 'section' ? item.multi_attributes : item.attributes;
      const attributeChanges = replaceExisting ? createReplacingItemAttributesMutation(existingAttributes, attributes) : attributes;

      Object.assign(jobUpdate, composeItemAttributesUpdate(itemType, itemId, item, attributeChanges, { jobStyles }));
    }

    // Perform the update
    if (Object.keys(jobUpdate).length > 0) await this.#performUpdate(this.#jobPath, jobUpdate);
    return { missingItems };
  }

  /**
   * Uploads a photo to the job. Returns a promise that resolves with the photo ID once the upload is complete and the job has been updated.
   * @param {Uint8Array} photoByteArray - The byte array representing the photo data.
   * @param {string} photoExtension - The file extension of the photo (e.g., 'jpg', 'png').
   * @param {PhotoType} photoType - The type of the photo (e.g., 'full', 'large').
   * @param {object} [options] - Additional options for the photo upload.
   * @param {boolean} [options.cloneable_photo] - Whether the photo is cloneable.
   * @param {boolean} [options.azmyth_photo] - Whether the photo is an azmyth photo.
   * @param {object} [options.exifOverrideValue] - EXIF data to override the extracted data.
   * @returns {Promise<string>}
   */
  async uploadPhoto(photoByteArray, photoExtension, photoType, options) {
    if ([photoByteArray, photoExtension, photoType].some((entry) => !entry))
      throw new Error('Could not upload photo because required data is missing');

    // Get a unique ID (v4 UUID) and filename for the photo
    const photoId = crypto.randomUUID();
    let photoFileName = getPhotoFileName(photoId, photoExtension, photoType);

    // Get an SDK-agnostic storage service
    const clientOrAdminStorage = getClientOrAdminStorage(this.firebase);

    // Upload the photo to the bucket
    await clientOrAdminStorage.ref(`photos/${photoFileName}`).put(photoByteArray);

    // Re-pack the byte array into a File object so we can extract EXIF data; we'll write this data to the job below
    const fileBuffer = new Uint8Array(photoByteArray).buffer;
    const photoFile = new File([fileBuffer], photoFileName, { type: `image/${photoExtension}` });
    // Add file name to EXIF data
    if (options?.exifOverrideValue) {
      options.exifOverrideValue.filename = photoFileName;
      options.exifOverrideValue.name_date = photoFileName + '|' + options.exifOverrideValue.date_taken;
    }
    /** @type {{ cloneable_photo?: boolean, azmyth_photo?: boolean, [key: string]: any }} */
    const newPhotoData = options?.exifOverrideValue ?? (await GetExif(photoFile)) ?? {};

    // Add additional photo data from the options
    if (options && options.cloneable_photo) newPhotoData.cloneable_photo = true;
    if (options && options.azmyth_photo) newPhotoData.azmyth_photo = true;

    // Note: This line is found in the beginUpload function of the katapult-upload-page.js and in the _toPhotosRecordArray function of
    // UploadWorker.js file. Since API uploads should work similar to these pages AND it fixes an issue with rotation, it's included here.
    delete newPhotoData.rotation;

    // Update the photo data on the job
    const addPhotoUpdate = { [`photos/${photoId}`]: newPhotoData };
    await this.#performUpdate(this.#jobPath, addPhotoUpdate);

    return photoId;
  }

  /**
   * Unassociates a photo from an item
   * @param {string} photoId - The id of the photo to be unassociated.
   * @param {string} itemId - The id of the item to be unassociated from.
   * @param {string} itemType - The type of the item (e.g., 'node', 'section').
   * @returns {Promise<void>} Resolves when the photo has been unassociated from the item.
   */
  async unassociatePhotoFromItem(photoId, itemId, itemType) {
    // Check to see if a photo exists
    if (!photoId || !itemId || !itemType) throw new Error('Could not unnassociate photo from item because required data is missing');

    // Unassociate the photo
    const associationsToBreak = [{ itemId, itemType, photoId }];
    const jobPhotos = await this.getPhotos();
    const nodePhotoUnassociationUpdate = composePhotoUnassociationUpdate(associationsToBreak, { jobPhotos, itemsDeleted: false });
    await this.#performUpdate(this.#jobPath, nodePhotoUnassociationUpdate);
  }

  /**
   * Unassociates a photo from all of its associated locations
   * @param {string} photoId - The id of the photo to be unassociated.
   * @param {object} associatedLocations - The locations to be unassociated from the photo.
   */
  async unassociatePhotoFromAssociatedLocations(photoId, associatedLocations) {
    if (!photoId || !associatedLocations)
      throw new Error('Could not unnassociate photo from associated locations because required data is missing');

    // Unassociate the photo from all of its associated locations
    const jobPhotos = await this.getPhotos();
    const associationsToBreak = Object.entries(associatedLocations).map(([itemId, itemType]) => {
      return { itemId, itemType, photoId };
    });
    const nodePhotoUnassociationUpdate = composePhotoUnassociationUpdate(associationsToBreak, { jobPhotos, itemsDeleted: false });
    await this.#performUpdate(this.#jobPath, nodePhotoUnassociationUpdate);
  }

  /**
   * Associates a photo to a node with the specified association type.
   * @param {string} nodeId - The id of the node to associate the photo with.
   * @param {string} photoId - The id of the photo to be associated with the node.
   * @param {AssociationValue} associationValue - The type of association.
   * @returns {Promise<void>} Resolves when the association is successfully updated.
   */
  async associatePhotoToNode(nodeId, photoId, associationValue) {
    // Check to see if a photo and node exists before the association
    const nodePromise = this.getNodeById(nodeId);
    const photoPromise = this.getPhotoById(photoId);
    const [node, photo] = await Promise.all([nodePromise, photoPromise]);
    if (!node || !photo || associationValue === undefined)
      throw new Error('Could not associated photo to node because required data is missing or invalid');

    // Unassociate the photo from it's current associated locations so a 1:1 relationship is maintained
    if (photo.associated_locations) await this.unassociatePhotoFromAssociatedLocations(photoId, photo.associated_locations);

    // Associate the photo
    const nodePhotoAssociationUpdate = composeItemPhotoAssociationUpdate('node', nodeId, photoId, associationValue);
    await this.#performUpdate(this.#jobPath, nodePhotoAssociationUpdate);
  }

  /**
   * Associates a photo to a section with the specified association type.
   * @param {string} sectionId - The id of the section to associate the photo with.
   * @param {string} connectionId - The id of the connection that the section belongs to.
   * @param {string} photoId - The id of the photo to be associated with the section.
   * @param {AssociationValue} associationValue - The type of association.
   * @returns {Promise<void>} Resolves when the association is successfully updated.
   */
  async associatePhotoToSection(sectionId, connectionId, photoId, associationValue) {
    // Check to see if a photo and node exists before the association
    const sectionPromise = this.getSectionByIds(connectionId, sectionId);
    const photoPromise = this.getPhotoById(photoId);
    const [section, photo] = await Promise.all([sectionPromise, photoPromise]);
    if (!section || !photo || associationValue === undefined)
      throw new Error('Could not associated photo to section because required data is missing');

    // Unassociate the photo from it's current associated locations so a 1:1 relationship is maintained
    if (photo.associated_locations) await this.unassociatePhotoFromAssociatedLocations(photoId, photo.associated_locations);

    // Associate the photo
    const itemId = `${connectionId}:${sectionId}`;
    const nodePhotoAssociationUpdate = composeItemPhotoAssociationUpdate('section', itemId, photoId, associationValue);
    await this.#performUpdate(this.#jobPath, nodePhotoAssociationUpdate);
  }

  /**
   * Changes the association value of a photo that is already associated to a node, without disturbing the rest of
   * the node's photo record. Promoting to 'main' or 'old_main' demotes whichever photo currently holds it.
   * @param {string} nodeId - The id of the node the photo is associated to.
   * @param {string} photoId - The id of the photo whose association is changing.
   * @param {'main'|'old_main'|true} associationValue - The new association value.
   * @returns {Promise<void>} Resolves when the association value is successfully updated.
   */
  async setNodePhotoAssociationValue(nodeId, photoId, associationValue) {
    if (associationValue === undefined) throw new Error('Could not set the photo association because the association value is missing');

    // Run the read, demotion, and promotion inside a transaction so two rapid promotions can't both demote the same
    // old holder and leave two photos holding `main`/`old_main`. Reusing the composer keeps the demotion logic in one place.
    const prefix = `nodes/${nodeId}/photos/`;
    const { committed } = await this.reference.child(`nodes/${nodeId}/photos`).transaction((photos) => {
      if (!photos?.[photoId]) return; // Photo is not on the node; abort without writing.
      const associationValueUpdate = composePhotoAssociationValueUpdate('node', nodeId, photoId, associationValue, photos);
      for (const [path, value] of Object.entries(associationValueUpdate)) {
        const affectedPhotoId = path.slice(prefix.length).split('/')[0];
        const existing = photos[affectedPhotoId];
        photos[affectedPhotoId] = typeof existing === 'string' ? { association: value } : { ...existing, association: value };
      }
      return photos;
    });
    if (!committed) throw new Error('Could not set the photo association because the photo is not on the node');
  }

  /**
   * Get the job's photos.
   * @returns {Promise<any>} The job's photos.
   */
  async getPhotos() {
    return await this.#readDataOnce('photos');
  }

  /**
   * Listen for changes to the job's photos.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's photos change.
   * @returns {() => void} A function to remove the listener.
   */
  onPhotosSnapshot(callback) {
    return this.#setUpListener('photos', callback);
  }

  /**
   * Get a specific photo from the job.
   * @param {string} photoId - The id (key) of the photo.
   * @returns {Promise<any>} The photo.
   */
  async getPhotoById(photoId) {
    return await this.#readDataOnce(`photos/${photoId}`);
  }

  /**
   * Get a specific photo folder from the job.
   * @param {string} folderId - The id (key) of the photo folder.
   * @returns {Promise<Record<string, any>|null>} The photo folder, or null when it does not exist.
   */
  async getPhotoFolderById(folderId) {
    return await this.#readDataOnce(`photo_folders/${folderId}`);
  }

  /**
   * Get the job's photo summary object.
   * @returns {Promise<any>} The job's photo summary object.
   */
  async getPhotoSummary() {
    return await this.#readDataOnce('photo_summary');
  }

  /**
   * Listen for changes to the job's photo summary object.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's photo summary object changes.
   * @returns {() => void} A function to remove the listener.
   */
  onPhotoSummarySnapshot(callback) {
    return this.#setUpListener('photo_summary', callback);
  }

  /**
   * Get the photo summary record for a specific photo in the job.
   * @param {string} photoId - The id (key) of the photo.
   * @returns {Promise<any>} The photo summary record.
   */
  async getPhotoSummaryById(photoId) {
    return await this.#readDataOnce(`photo_summary/${photoId}`);
  }

  /**
   * Get the calibration anchors for a photo in the job.
   * @param {string} photoId - Id of the photo.
   * @returns {Promise<any>} Photo's calibration anchors.
   */
  async getPhotoCalibrationAnchors(photoId) {
    // Validate the function input
    if (typeof photoId !== 'string') throw new Error('Photo id must be a string');

    // Get the photo
    const photo = await this.getPhotoById(photoId);
    // If the photo doesn't exist, throw an error
    if (photo == null) throw new Error(`Photo not found: ${photoId}`);

    return photo.photofirst_data?.anchor_calibration ?? null;
  }

  /**
   * Get a specific calibration anchor for a photo in the job.
   * @param {string} photoId - Id of the photo.
   * @param {string} anchorId - Id of the calibration anchor.
   * @returns {Promise<any>} Photo's calibration anchor.
   */
  async getPhotoCalibrationAnchorById(photoId, anchorId) {
    // Validate the function input
    if (typeof photoId !== 'string' || typeof anchorId !== 'string') throw new Error('Photo and marker ids must be strings');

    // Get the calibration anchors
    const calibrationAnchors = await this.getPhotoCalibrationAnchors(photoId);

    return calibrationAnchors?.[anchorId] ?? null;
  }

  /**
   * Add a calibration anchor to a photo in the job.
   * @param {string} photoId - The id of the photo.
   * @param {PixelSelection} pixelSelection - The pixel selection for the photo.
   * @param {string} height - The height to assign to the calibration anchor.
   * @param {object} [options]
   * @param {string} [options.newMarkerId] - The id to use for the new calibration anchor. If not provided, a new id will be generated.
   * @param {boolean} [options.forciblyDeleteStickAlign] - Whether to forcibly delete the stick align object from the photo, requiring the
   * photo to be viewed in Katapult Pro for re-calibration.
   * @returns {Promise<string>} A promise that resolves to the id of the calibration anchor when the create has finished.
   */
  async addPhotoCalibrationAnchor(photoId, pixelSelection, height, options = {}) {
    const normalizedPixelSelection = normalizePixelSelection(pixelSelection);
    const { newMarkerId: newMarkerIdFromOptions } = options;
    const newMarkerId = newMarkerIdFromOptions ?? String(this.reference.push().key);
    return await this.#updatePhotoCalibrationAnchor(photoId, newMarkerId, { height, pixel_selection: normalizedPixelSelection }, options);
  }

  /**
   * Update a calibration anchor on a photo in the job.
   * @param {string} photoId - The id of the photo.
   * @param {string} markerId - The id of the calibration anchor.
   * @param {PixelSelection} pixelSelection - The pixel selection for the photo.
   * @param {string} height - The height to assign to the calibration anchor.
   * @param {object} [options]
   * @param {boolean} [options.forciblyDeleteStickAlign] - Whether to forcibly delete the stick align object from the photo, requiring the
   * photo to be viewed in Katapult Pro for re-calibration.
   * @returns {Promise<string>} A promise that resolves to the id of the calibration anchor when the update has finished.
   */
  async updatePhotoCalibrationAnchor(photoId, markerId, pixelSelection, height, options = {}) {
    const normalizedPixelSelection = normalizePixelSelection(pixelSelection);
    return await this.#updatePhotoCalibrationAnchor(photoId, markerId, { height, pixel_selection: normalizedPixelSelection }, options);
  }

  /**
   * Remove a calibration anchor from a photo in the job.
   * @param {string} photoId - The id of the photo.
   * @param {string} markerId - The id of the calibration anchor.
   * @param {object} [options]
   * @param {boolean} [options.forciblyDeleteStickAlign] - Whether to forcibly delete the stick align object from the photo, requiring the
   * photo to be viewed in Katapult Pro for re-calibration.
   * @returns {Promise<string>} A promise that resolves to the id of the calibration anchor when the removal has finished.
   */
  async removePhotoCalibrationAnchor(photoId, markerId, options = {}) {
    return await this.#updatePhotoCalibrationAnchor(photoId, markerId, null, options);
  }

  /**
   * Internal method to update a calibration anchor on a photo in the job.
   * @param {string} photoId - The id of the photo.
   * @param {string} markerId - The id of the calibration anchor.
   * @param {{ height: string, pixel_selection: PixelSelection }|null} anchorMarkerData - The new data for the calibration anchor (or null
   * if the anchor marker should be deleted)
   * @param {object} [options]
   * @param {boolean} [options.forciblyDeleteStickAlign] - Whether to forcibly delete the stick align object from the photo, requiring the
   * photo to be viewed in Katapult Pro for re-calibration.
   * @returns {Promise<string>} A promise that resolves to the calibration anchor id when the update has finished.
   */
  async #updatePhotoCalibrationAnchor(photoId, markerId, anchorMarkerData, options = {}) {
    // Validate the function input
    if (typeof photoId !== 'string' || typeof markerId !== 'string') throw new Error('Photo and marker ids must be strings');
    const anchorMarkerDataIsValid =
      anchorMarkerData === null ||
      (typeof anchorMarkerData === 'object' && 'height' in anchorMarkerData && 'pixel_selection' in anchorMarkerData);
    if (!anchorMarkerDataIsValid) throw new Error('Anchor marker data must be an object');

    // Fetch all of the data needed to create the update
    const [photo, jobModel, jobTraces, jobNodes, jobConnections, jobStyles] = await Promise.all([
      this.getPhotoById(photoId),
      this.getModel(),
      this.getTraces(),
      this.getNodes(),
      this.getConnections(),
      this.getJobStyles()
    ]);
    // If the photo doesn't exist, throw an error
    if (photo == null) throw new Error(`Photo not found: ${photoId}`);

    // Get the item associated with the photo
    const itemRecord = getPhotoAssociatedItemRecord(photo, jobNodes, jobConnections);
    // If the item doesn't exist, throw an error
    if (itemRecord == null) throw new Error(`Unable to find item associated with photo: ${photoId}`);

    // Get the model associated with the photo
    const model = KatapultModel.fromId(jobModel, { firebaseApp: this.firebase });
    const [input_models, config, make_ready, authData] = await Promise.all([
      model.getInputModels(),
      model.getConfig(),
      model.getMakeReady(),
      getAuthData(this.firebase)
    ]);

    const modelData = { input_models, config, make_ready };
    const { uid: userUid } = authData ?? {};

    const clonedAnchorMarkerData = structuredClone(anchorMarkerData);
    if (clonedAnchorMarkerData?.pixel_selection) {
      clonedAnchorMarkerData.pixel_selection = normalizePixelSelection(clonedAnchorMarkerData.pixel_selection);
    }

    // Create the update object
    const jobUpdate = composeAnchorMarkerUpdate(
      photoId,
      markerId,
      clonedAnchorMarkerData,
      {
        ...itemRecord,
        photo,
        model: modelData,
        jobTraces,
        jobNodes,
        jobConnections,
        jobStyles,
        userUid
      },
      options
    );

    // Perform the update
    await this.#performUpdate(this.#jobPath, jobUpdate);
    return markerId;
  }

  /**
   * Get all of the elements for a photo in the job.
   * @param {string} photoId - Id of the photo.
   * @returns {Promise<Record<string, any>>} Promise that resolves to the photo elements.
   */
  async getPhotoElements(photoId) {
    // Validate the function input
    if (typeof photoId !== 'string') throw new Error('Photo id must be a string');

    const photo = await this.getPhotoById(photoId);
    // If the photo doesn't exist, throw an error
    if (photo == null) throw new Error(`Photo not found: ${photoId}`);

    return getFlatPhotoElementList(photo?.photofirst_data);
  }

  /**
   * Get a specific element for a photo in the job.
   * @param {string} photoId - Id of the photo.
   * @param {string} elementId - Id of the photo element.
   * @returns {Promise<any>} Promise that resolves to the photo element, or null if it doesn't exist.
   */
  async getPhotoElementById(photoId, elementId) {
    // Validate the function input
    if (typeof photoId !== 'string' || typeof elementId !== 'string') throw new Error('Photo and element ids must be strings');

    // Get all of the elements for the photo
    const allElements = await this.getPhotoElements(photoId);

    return allElements[elementId] ?? null;
  }

  /**
   * Calculate the make ready clearance violations for every element in a photo.
   * @param {string} photoId - Id of the photo.
   * @returns {Promise<Record<string, any>>} The MR violations for the photo, keyed by photo element id
   */
  async getPhotoMRViolations(photoId) {
    if (typeof photoId !== 'string') throw new Error('Photo id must be a string');

    const [photo, jobModel] = await Promise.all([this.getPhotoById(photoId), this.getModel()]);
    if (photo == null) throw new Error(`Photo not found: ${photoId}`);

    const [itemId, itemType] = Object.entries(photo?.associated_locations ?? {})[0] ?? [];
    if (itemId == null) return {};
    const [nodeOrConnectionId, sectionKey] = itemId.split(':');

    // Effective moves on a section photo are spread between the endpoints of the connection, which requires the full node and connection lists to calculate the distance ratios
    const photoIsOnASection = itemType === 'section';

    const model = KatapultModel.fromId(jobModel, { firebaseApp: this.firebase });
    const [item, jobTraces, makeReady, modelConfig, jobNodes, jobConnections] = await Promise.all([
      itemType === 'node' ? this.getNodeById(nodeOrConnectionId) : this.getSectionByIds(nodeOrConnectionId, sectionKey),
      this.getTraces(),
      model.getMakeReady(),
      model.getConfig(),
      photoIsOnASection ? this.getNodes() : undefined,
      photoIsOnASection ? this.getConnections() : undefined
    ]);
    if (item == null) return {};

    const effectiveMovesDistanceRatios = photoIsOnASection
      ? CalcPhotoDistanceRatios(nodeOrConnectionId, sectionKey, jobNodes, jobConnections)
      : undefined;

    const makeReadyRules = /** @type {any} */ (makeReady)?.make_ready_items;
    const autoInsertGroundMarkerInPolePhoto = /** @type {any} */ (modelConfig)?.photofirst?.auto_insert_ground_marker_in_pole_photo;

    const nodeData = getClearanceNodeData({
      itemType,
      item,
      itemId,
      connectionId: nodeOrConnectionId,
      nodes: jobNodes,
      connections: jobConnections
    });

    const { MRItems } = calcPhotoClearances(
      item,
      structuredClone(photo),
      photoId,
      itemType,
      { traces: jobTraces?.trace_data, makeReadyRules, nodeData },
      { effectiveMovesDistanceRatios, autoInsertGroundMarkerInPolePhoto }
    );

    return MRItems;
  }

  /**
   * Create a photo element on a photo in the job.
   * @param {string} photoId - Id of the photo to create the element on.
   * @param {PhotoElementCreateParams} elementData - The data to create the element with.
   * @returns {Promise<string>} The id of the created element.
   */
  async createPhotoElement(photoId, elementData) {
    // Validate the function input
    if (typeof photoId !== 'string') throw new Error('Photo and marker ids must be strings');
    if (elementData != null && typeof elementData !== 'object') throw new Error('Element data must be an object');
    if (elementData?.element_type == null) throw new Error('Element type is required');

    const clonedElementData = structuredClone(elementData);
    if (clonedElementData?.pixel_selection) {
      clonedElementData.pixel_selection = normalizePixelSelection(clonedElementData.pixel_selection);
    }

    // Get the id for the element
    clonedElementData.id ??= this.reference.push().key;

    // TODO (2025-04-24): This is all copied from #updatePhotoElement. We should refactor this to avoid duplication.

    // Fetch all of the data needed to create the update
    const [photo, jobModel, jobTraces, jobNodes, jobConnections, jobStyles] = await Promise.all([
      this.getPhotoById(photoId),
      this.getModel(),
      this.getTraces(),
      this.getNodes(),
      this.getConnections(),
      this.getJobStyles()
    ]);
    // If the photo doesn't exist, throw an error
    if (photo == null) throw new Error(`Photo not found: ${photoId}`);

    // Get the item associated with the photo
    const itemRecord = getPhotoAssociatedItemRecord(photo, jobNodes, jobConnections);
    // If the item doesn't exist, throw an error
    if (itemRecord == null) throw new Error(`Unable to find item associated with photo: ${photoId}`);

    // Get the model associated with the photo
    const katapultModel = KatapultModel.fromId(jobModel, { firebaseApp: this.firebase });
    const [modelConfig, inputModels, makeReady] = await Promise.all([
      katapultModel.getConfig(),
      katapultModel.getInputModels(),
      katapultModel.getMakeReady()
    ]);
    const partialModel = { config: modelConfig, input_models: inputModels, make_ready: makeReady };

    const { uid: userUid } = (await getAuthData(this.firebase)) ?? {};

    // Create an update object and begin updating the photo marker's trace
    const jobUpdate = composePhotoElementCreate(photoId, clonedElementData, {
      ...itemRecord,
      photo,
      model: partialModel,
      jobTraces,
      jobNodes,
      jobConnections,
      jobStyles,
      userUid
    });

    // Perform the update
    await this.#performUpdate(this.#jobPath, jobUpdate);
    return clonedElementData.id;
  }

  /**
   * Update a photo element in the job.
   * @param {string} photoId - The id of the photo.
   * @param {string} elementId - The id of the photo element.
   * @param {PhotoElementUpdateParams} partialElementData - The data to update the element with.
   * @returns {Promise<void>} A promise that resolves when the photo element has been updated.
   */
  async updatePhotoElement(photoId, elementId, partialElementData) {
    // Update the photo element
    return await this.#updatePhotoElement(photoId, elementId, partialElementData);
  }

  async deletePhotoElement(photoId, elementId) {
    // Delete the photo element
    return await this.#updatePhotoElement(photoId, elementId, null);
  }

  /**
   * Update photo element data.
   * @param {string} photoId - Id of the photo.
   * @param {string} elementId - Id of the photo element.
   * @param {?PhotoElementUpdateParams} partialElementDataOrNull - The data to update the element with.
   * @returns {Promise<void>} A promise that resolves when the photo marker's trace has been updated.
   */
  async #updatePhotoElement(photoId, elementId, partialElementDataOrNull) {
    // Validate the function input
    if (typeof photoId !== 'string' || typeof elementId !== 'string') throw new Error('Photo and marker ids must be strings');
    // If the element data is not an object, throw an error
    if (partialElementDataOrNull != null && typeof partialElementDataOrNull !== 'object') throw new Error('Element data must be an object');

    const clonedPartialElementDataOrNull = structuredClone(partialElementDataOrNull);
    if (clonedPartialElementDataOrNull?.pixel_selection) {
      clonedPartialElementDataOrNull.pixel_selection = normalizePixelSelection(clonedPartialElementDataOrNull.pixel_selection);
    }

    // Fetch all of the data needed to create the update
    const [photo, jobModel, jobTraces, jobNodes, jobConnections, jobStyles] = await Promise.all([
      this.getPhotoById(photoId),
      this.getModel(),
      this.getTraces(),
      this.getNodes(),
      this.getConnections(),
      this.getJobStyles()
    ]);
    // If the photo doesn't exist, throw an error
    if (photo == null) throw new Error(`Photo not found: ${photoId}`);

    // Get the item associated with the photo
    const itemRecord = getPhotoAssociatedItemRecord(photo, jobNodes, jobConnections);
    // If the item doesn't exist, throw an error
    if (itemRecord == null) throw new Error(`Unable to find item associated with photo: ${photoId}`);

    // Get the model associated with the photo
    const katapultModel = KatapultModel.fromId(jobModel, { firebaseApp: this.firebase });
    const [modelConfig, inputModels, makeReady] = await Promise.all([
      katapultModel.getConfig(),
      katapultModel.getInputModels(),
      katapultModel.getMakeReady()
    ]);
    const partialModel = { config: modelConfig, input_models: inputModels, make_ready: makeReady };

    const { uid: userUid } = (await getAuthData(this.firebase)) ?? {};

    // Create an update object and begin updating the photo marker's trace
    const jobUpdate = composePhotoElementUpdate(photoId, elementId, clonedPartialElementDataOrNull, {
      ...itemRecord,
      photo,
      model: partialModel,
      jobTraces,
      jobNodes,
      jobConnections,
      jobStyles,
      userUid
    });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Get the job's traces, including both `trace_data` and `trace_items`.
   * @returns {Promise<JobTraces?>} The job's traces.
   */
  async getTraces() {
    return await this.#readDataOnce('traces');
  }

  /**
   * Listen for changes to the job's traces.
   * @param {OnSnapshotCallback<JobTraces>} callback - The callback function to be called when the job's traces change.
   * @returns {() => void} A function to remove the listener.
   */
  onTracesSnapshot(callback) {
    return this.#setUpListener('traces', callback);
  }

  /**
   * Get a specific trace definition from the job.
   * @param {string} traceId - The id of the trace.
   * @returns {Promise<TraceDefinition?>} The trace data.
   */
  async getTraceDataById(traceId) {
    return await this.#readDataOnce(`traces/trace_data/${traceId}`);
  }

  /**
   * Get the items associated with a job trace.
   * @param {string} traceId - The id of the trace.
   * @returns {Promise<TraceItemsList?>} The trace items.
   */
  async getTraceItemsById(traceId) {
    return await this.#readDataOnce(`traces/trace_items/${traceId}`);
  }

  /**
   * Set data for a trace in the job.
   * @param {string} traceId - The id of the trace.
   * @param {Record<string, any>|null} traceData - The data to set for the trace (or null to remove the trace).
   * @param {object} [options] - Options for the trace data update.
   * @param {boolean} [options.replaceExisting] - If `true`, any trace data not provided in `traceData` will be removed.
   * @returns {Promise<void>} A promise that resolves when the trace data has been set.
   */
  async setTraceData(traceId, traceData, options = {}) {
    const { replaceExisting = false } = options;

    // Fetch all of the data needed to create the update
    const [jobTraces, jobPhotos, jobNodes, jobConnections, jobStyles, jobModel] = await Promise.all([
      this.getTraces(),
      this.getPhotos(),
      this.getNodes(),
      this.getConnections(),
      this.getJobStyles(),
      this.getModel()
    ]);

    // If we're replacing existing values, set any existing value instances to null
    const traceDataMutations =
      traceData != null && replaceExisting ? createReplacingObjectMutation(jobTraces?.trace_data?.[traceId], traceData) : traceData;

    // Get the model associated with the job
    const model = KatapultModel.fromId(jobModel, { firebaseApp: this.firebase });
    console.log(`Model for trace data update: ${jobModel}`);
    const [input_models, config, make_ready, authData] = await Promise.all([
      model.getInputModels(),
      model.getConfig(),
      model.getMakeReady(),
      getAuthData(this.firebase)
    ]);

    const modelData = { input_models, config, make_ready };
    const { uid: userUid } = authData ?? {};

    // Create an update object
    const jobUpdate = composeTraceDataUpdate(traceId, traceDataMutations, {
      jobTraces,
      jobPhotos,
      jobNodes,
      jobConnections,
      jobStyles,
      model: modelData,
      userUid
    });

    // Perform the update
    return await this.#performUpdate(this.#jobPath, jobUpdate);
  }

  /**
   * Get the job's file records.
   * @returns {Promise<any>} The job's files.
   */
  async getFiles() {
    return await this.#readDataOnce('files');
  }

  /**
   * Listen for changes to the job's files.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's files change.
   * @returns {() => void} A function to remove the listener.
   */
  onFilesSnapshot(callback) {
    return this.#setUpListener('files', callback);
  }

  /**
   * Get the annotations of one of the job's map print configs.
   * @param {string} mapPrintConfigKey - The id (key) of the map print config.
   * @returns {Promise<any>} The annotations of the map print config.
   */
  async getMapPrintAnnotations(mapPrintConfigKey) {
    return await this.#readDataOnce(`map_print_config_layers/${mapPrintConfigKey}/annotations`);
  }

  /**
   * Update the annotations of one of the job's map print configs.
   * Seeding or clearing annotations can touch every node and connection in the job, so the update is split
   * into batches to keep any single write from growing too large.
   * @param {string} mapPrintConfigKey - The id (key) of the map print config.
   * @param {Record<string, any>} update - Paths relative to the config's annotations, mapped to their new values.
   * @returns {Promise<void>} A promise that resolves when every batch has been written.
   */
  async updateMapPrintAnnotations(mapPrintConfigKey, update) {
    const path = `${this.#jobPath}/map_print_config_layers/${mapPrintConfigKey}/annotations`;
    const entries = Object.entries(update);
    for (let index = 0; index < entries.length; index += KatapultJob.MAP_PRINT_ANNOTATION_BATCH_SIZE) {
      const batch = Object.fromEntries(entries.slice(index, index + KatapultJob.MAP_PRINT_ANNOTATION_BATCH_SIZE));
      await this.#performUpdate(path, batch);
    }
  }

  /**
   * Get the job's geohash.
   * @returns {Promise<any>} The geohash for the job.
   */
  async getGeohash() {
    return await this.#readDataOnce('geohash');
  }

  /**
   * Listen for changes to the job's geohash.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's geohash changes.
   * @returns {() => void} A function to remove the listener.
   */
  onGeohashSnapshot(callback) {
    return this.#setUpListener('geohash', callback);
  }

  /**
   * Get the data at a specific path within the job (relative to the job root).
   * @param {string} path - The path to read.
   * @returns {Promise<any>} The data at the given path.
   */
  async getDataAtPath(path) {
    return await this.#readDataOnce(path);
  }

  /**
   * Get the job's sharing object.
   * @returns {Promise<Record<string,string>>} The job's sharing object.
   */
  async getSharing() {
    return await this.#readDataOnce('sharing');
  }

  /**
   * Listen for changes to the job's sharing object.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the job's sharing object changes.
   * @returns {() => void} A function to remove the listener.
   */
  onSharingSnapshot(callback) {
    return this.#setUpListener('sharing', callback);
  }

  /**
   * Set the job's sharing object (replacing the current sharing object).
   * @param {Record<string,string|null>|null} sharing - An object mapping company ids to permissions, or `null` to un-share the job with all companies.
   * @returns {Promise<void>} A promise that resolves when the sharing object has been set.
   */
  async setSharing(sharing) {
    // Validate the function input
    if (sharing != null) {
      if (typeof sharing !== 'object') throw new Error('Sharing object must be an object');
      if (Object.values(sharing).some((permission) => !JOB_SHARING_PERMISSIONS.includes(permission))) throw new Error('Unknown permission');
    }

    // Get the current job sharing object
    const currentSharing = await this.getSharing();

    // Determine which companies need to be added, changed, or removed
    const companyList = [...new Set([...Object.keys(currentSharing ?? {}), ...Object.keys(sharing ?? {})])];
    const companiesToModify = companyList.filter((companyId) => currentSharing?.[companyId] !== sharing?.[companyId]);

    // Get the current job data
    const [jobDetails, jobSharing, metadata] = await Promise.all([this.getDetails(), this.getSharing(), this.getMetadata()]);

    // Get the job chooser options for the owner
    const ownerJobChooserOptions = (
      await this.database.ref(`photoheight/company_space/${jobDetails.owner}/options/job_chooser`).once('value')
    ).val();

    // Create the update object
    const photoheightUpdate = companiesToModify.reduce((update, companyId) => {
      const permission = sharing?.[companyId] ?? null;
      const jobChooserOptions = companyId === jobDetails.owner ? ownerJobChooserOptions : null;
      const partialUpdate = composeCompanyPermissionUpdate(companyId, permission, {
        jobId: this.id,
        jobDetails,
        jobSharing,
        metadata,
        jobChooserOptions
      });
      return { ...update, ...partialUpdate };
    }, {});

    // If the update object is empty, do nothing
    if (Object.keys(photoheightUpdate).length === 0) return;

    // Perform the update
    return await this.#performUpdate('photoheight', photoheightUpdate);
  }

  /**
   * Update the job's sharing object (modifying only those companies that are specified).
   * @param {Record<string,string|null>} sharing - An object mapping company ids to permissions.
   * @returns {Promise<void>} A promise that resolves when the sharing object has been updated.
   */
  async updateSharing(sharing) {
    // Validate the function input
    if (sharing == null || typeof sharing !== 'object') throw new Error('Sharing object must be an object');
    if (Object.keys(sharing).length === 0) throw new Error('Sharing object must contain at least one company');
    if (Object.values(sharing).some((permission) => !JOB_SHARING_PERMISSIONS.includes(permission))) throw new Error('Unknown permission');
    // Get the current job data
    const [jobDetails, jobSharing, metadata] = await Promise.all([this.getDetails(), this.getSharing(), this.getMetadata()]);
    const folderSharing = await GetFolderSharing(jobDetails?.projectFolder, jobDetails.owner, { firebase: this.firebase });

    // Get the job chooser options for the owner
    const ownerJobChooserOptions = (
      await this.database.ref(`photoheight/company_space/${jobDetails.owner}/options/job_chooser`).once('value')
    ).val();

    // Create an update object and begin updating the sharing object
    const photoheightUpdate = Object.entries(sharing).reduce((update, [companyId, permission]) => {
      if (folderSharing?.[companyId]) return update;
      const jobChooserOptions = companyId === jobDetails.owner ? ownerJobChooserOptions : null;
      const partialUpdate = composeCompanyPermissionUpdate(companyId, permission, {
        jobId: this.id,
        jobDetails,
        jobSharing,
        metadata,
        jobChooserOptions
      });
      return { ...update, ...partialUpdate };
    }, {});

    // If the update object is empty, do nothing
    if (Object.keys(photoheightUpdate).length === 0) return;

    // Perform the update
    return await this.#performUpdate('photoheight', photoheightUpdate);
  }

  /**
   * Update the job sharing permission for a specific company.
   * @param {string} companyId - The id of the company to update the permission for.
   * @param {string|null} permission - The new permission to give the company (one of `'read'`, `'write'`, `'applicant'`, or `null`).
   * Setting the permission to `null` will un-share the job with the company.
   * @returns {Promise<void>} A promise that resolves when the permission has been updated.
   */
  async updateCompanyPermission(companyId, permission) {
    // Validate the function input
    if (companyId == null) throw new Error('Company id must be specified');
    if (!JOB_SHARING_PERMISSIONS.includes(permission)) throw new Error(`Unknown permission: ${permission}`);

    // Get the current job data
    const [jobDetails, jobSharing, metadata] = await Promise.all([this.getDetails(), this.getSharing(), this.getMetadata()]);

    // If this company is the job owner, fetch the job chooser options
    const jobChooserOptions =
      companyId == jobDetails.owner
        ? (await this.database.ref(`photoheight/company_space/${companyId}/options/job_chooser`).once('value')).val()
        : null;

    // Compose the multi-location update object
    const photoheightUpdate = composeCompanyPermissionUpdate(companyId, permission, {
      jobId: this.id,
      jobDetails,
      jobSharing,
      metadata,
      jobChooserOptions
    });

    // If the update object is empty, do nothing
    if (Object.keys(photoheightUpdate).length === 0) return;

    // Perform the update
    return await this.#performUpdate('photoheight', photoheightUpdate);
  }

  /**
   * Fetches data from the specified paths in the job data.
   * @param {string[]} paths
   * @param {Object} options
   * @param {boolean} [options.includeNullValues=false] - If true, include null values in the result.
   * @returns {Promise<Record<string, any>>} - A promise that resolves to an object containing the data at the specified paths.
   */
  async getDataAtPaths(paths, { includeNullValues = false } = {}) {
    const unaliasedPaths = paths.map((path) => KatapultJob.JOB_DATA_PATH_ALIASES[path] ?? path);
    const filteredPaths = unaliasedPaths.filter((path) => KatapultJob.JOB_DATA_PATHS.includes(path));
    const deduplicatedPaths = [...new Set(filteredPaths)];
    const result = {};
    await Promise.all(
      deduplicatedPaths.map(async (path) => {
        const data = await this.#readDataOnce(path);
        if (data == null && !includeNullValues) return;
        result[path] = data;
      })
    );
    return result;
  }

  /**
   * Set up a no-op listener to cache job data in memory (does nothing if the listener has already been set up).
   * @param {string} path - The path to listen to.
   */
  #setUpCachingListener(path) {
    // Do nothing if the listener has already been set up.
    if (this.#cachingListenersByPath[path]) return;

    // Do nothing if there is a caching listener at a shallower path.
    const pathParts = path.split('/').slice(0, -1);
    for (const index in pathParts) {
      const parentPath = pathParts.slice(0, Number(index) + 1).join('/');
      if (this.#cachingListenersByPath[parentPath]) return;
    }

    // Set up the no-op listener.
    const childRef = this.reference.child(path);
    const listener = childRef.on('value', () => {});
    this.#cachingListenersByPath[path] = listener;

    // If there are caching listeners on a child path of this path, remove them.
    const childPathsWithListeners = Object.keys(this.#cachingListenersByPath).filter((childPath) => childPath.startsWith(`${path}/`));
    for (const childPath of childPathsWithListeners) {
      this.reference.child(childPath).off('value', this.#cachingListenersByPath[childPath]);
      delete this.#cachingListenersByPath[childPath];
    }
  }

  /**
   * Sets up a listener for a child path.
   * @param {string} childPath - The child path to listen to.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the child path changes.
   * @returns {() => void} A function to remove the listener.
   */
  #setUpListener(childPath, callback) {
    // If desired, cache job data for this path.
    if (this.#autoCache) this.#setUpCachingListener(childPath);

    // Set up the specified listener.
    const childRef = this.reference.child(childPath);
    childRef.on('value', callback);

    // Keep a record of the listener, and return a function to remove it.
    this.#listenersByPath[childPath] ??= [];
    this.#listenersByPath[childPath].push(callback);
    const listenerUnsubscriber = () => {
      childRef.off('value', callback);
      this.#listenersByPath[childPath] = this.#listenersByPath[childPath].filter((listener) => listener !== callback);
    };

    return listenerUnsubscriber;
  }

  /**
   * Reads data from a path once (uses data from other listeners if they are available).
   * @param {string} path - The path to read from.
   * @returns {Promise<any>} The data at the path.
   */
  async #readDataOnce(path) {
    // If desired, cache job data for this path.
    if (this.#autoCache) this.#setUpCachingListener(path);

    // Read the data from the path.
    const snapshot = await this.reference.child(path).once('value');
    return snapshot.val();
  }
}
